/* GBI bundles and kit picker (B4). v2 spec §B4, §C.6; v3 delta §4.
   <gbi-bundle-tiers>  boxed tier cards + one summary panel in the buy box (owns the bundle state)
   <gbi-bundle-picker> the kit picker inside <dialog class="gbi-sheet">: This kit, then a
                       browse rail (search, Recommended, collection chips) over one paged list.
   Vanilla ES2019, no libraries. Every visible string comes from the Bundle config. */
(() => {
  if (window.__gbiBundle) return;
  window.__gbiBundle = true;

  /* ------------------------------------------------------------------ helpers */
  const G = () => window.GBI || {};
  const tpl = (str, vars) => {
    const g = G();
    if (typeof g.t === 'function') return g.t(str == null ? '' : String(str), vars || {});
    return String(str == null ? '' : str).replace(/\[(\w+)\]/g, (m, k) => (vars && vars[k] != null ? String(vars[k]) : m));
  };
  const money = (cents) => {
    const g = G();
    if (g.money && typeof g.money.format === 'function') return g.money.format(cents);
    return (cents / 100).toFixed(2);
  };
  const track = (event, params) => {
    try { const g = G(); if (typeof g.track === 'function') g.track(event, params || {}); } catch (_) { /* never break UI */ }
  };
  const reduced = () => {
    const g = G();
    if (g.mq && g.mq.reduced) return g.mq.reduced.matches;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };
  const isDesktop = () => {
    const g = G();
    if (g.mq && g.mq.desktop) return g.mq.desktop.matches;
    return window.matchMedia('(min-width: 990px)').matches;
  };
  const isWide = () => window.matchMedia('(min-width: 750px)').matches;
  const readJSON = (el) => {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (_) { return null; }
  };
  const hasVersions = (p) => !!(p && p.variants && !(p.variants.length === 1 && /^default title$/i.test(String(p.variants[0].title || ''))));
  // V3D-4: a version choice only when there is more than one variant to pick from.
  const isMulti = (p) => !!(p && p.variants && p.variants.length > 1);
  // V3D-5: JS mirror of snippets/gbi-clean-title.liquid (strip: true).
  const cleanTitle = (title) => {
    const t = String(title || '').trim();
    let words = t.split(/\s+/);
    if (words.length > 1 && words[0].toLowerCase() === 'gobuildit') words = words.slice(1);
    const last = (words[words.length - 1] || '').toLowerCase();
    let drop = 0;
    if (last.length > 3 && /^[0-9].*pcs$/.test(last)) drop = 1;
    else if (last === 'pcs' && words.length > 1 && /^[0-9]+$/.test(words[words.length - 2])) drop = 2;
    if (drop && words.length > drop) words = words.slice(0, words.length - drop);
    let out = words.join(' ');
    for (let i = 0; i < 2; i += 1) {
      if (/ [-|–]$/.test(out)) out = out.slice(0, -2).trim();
      else if (/,$/.test(out)) out = out.slice(0, -1).trim();
    }
    return out || t;
  };
  const minAvailablePrice = (p) => {
    const prices = (p.variants || []).filter((v) => v.available).map((v) => v.price);
    return prices.length ? Math.min.apply(null, prices) : (p.variants && p.variants[0] ? p.variants[0].price : 0);
  };
  const firstAvailable = (p) => (p.variants || []).find((v) => v.available) || null;
  // 2026-10-22 sale prices: a variant's compare-at, only while sale prices are on (cfg.saleOn) and above its price.
  const cmpOf = (cfg, v) => (cfg && cfg.saleOn && v && Number(v.compare_at_price) > Number(v.price) ? Number(v.compare_at_price) : null);
  // 2026-10-22 sale prices: the variant behind minAvailablePrice (first cheapest available, else the first one).
  const cheapestAvailable = (p) => {
    const vs = (p.variants || []).filter((v) => v.available);
    if (!vs.length) return (p.variants && p.variants[0]) || null;
    return vs.reduce((a, v) => (v.price < a.price ? v : a), vs[0]);
  };
  const setText = (el, text) => { if (el && el.textContent !== text) el.textContent = text; };
  const show = (el, on) => { if (el) el.hidden = !on; };
  const ariaDisable = (el, on) => {
    if (!el) return;
    if (on) el.setAttribute('aria-disabled', 'true');
    else el.removeAttribute('aria-disabled');
  };
  const isAriaDisabled = (el) => !!el && el.getAttribute('aria-disabled') === 'true';
  const productRootOf = (el, sid) => el.closest('gbi-product') || (sid ? document.getElementById('GbiProduct-' + sid) : null);
  const SELECT_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Up', 'Down', 'Left', 'Right', ' ', 'Spacebar', 'Enter', 'Home', 'End'];

  /* ------------------------------------------------------------------ engine (§C.6, pure) */
  /* 2026-10-22 sale prices: a kit is a unit { price, compareAt } (cart kits from /cart.js stay plain
     numbers: no compare-at). sum / saving / total run on the current prices exactly as before (the
     BOGOS discount, to the cent). wasSum = Σ (compareAt || price) and sale = wasSum − sum only add
     what the compare-at prices show on top; they never feed the bundle saving. */
  const unitPrice = (u) => (typeof u === 'number' ? u : Number(u && u.price) || 0);
  const unitWas = (u) => {
    const p = unitPrice(u);
    const c = u && typeof u === 'object' ? Number(u.compareAt) || 0 : 0;
    return c > p ? c : p;
  };
  const Engine = {
    pctFor(cfg, kits) {
      return kits < 2 ? 0 : (Number(cfg.pcts && cfg.pcts[String(Math.min(kits, cfg.maxTier))]) || 0);
    },
    compute(cfg, units) {
      const prices = units.map(unitPrice);
      const kits = prices.length;
      const pct = Engine.pctFor(cfg, kits);
      const sum = prices.reduce((a, b) => a + b, 0);
      let saving = 0;
      if (pct > 0 && cfg.mode === 'cheapest_one') saving = Math.floor(Math.min.apply(null, prices) * pct / 100);
      else if (pct > 0 && cfg.mode === 'all_kits') saving = prices.reduce((a, p) => a + Math.floor(p * pct / 100), 0);
      const wasSum = units.reduce((a, u) => a + unitWas(u), 0);
      return { kits, pct, sum, saving, total: sum - saving, wasSum, sale: wasSum - sum };
    },
    fillPrices(main, units, n) {
      const extras = units.slice(0, Math.max(0, n - 1));
      while (extras.length < n - 1) extras.push(main);
      return [main].concat(extras);
    },
    fill(cfg, main, units, n, cartPrices) {
      return Engine.withCart(cfg, Engine.fillPrices(main, units, n), cartPrices);
    },
    /* v3.9: BOGOS discounts the whole cart. With kits already in the cart, the honest figure for
       these kits is the extra saving they unlock: saving(cart + these) − saving(cart alone).
       2026-10-22 sale prices: wasSum / sale count these kits only (cart kits have no compare-at). */
    withCart(cfg, units, cartPrices) {
      if (!cartPrices || !cartPrices.length) return Engine.compute(cfg, units);
      const base = Engine.compute(cfg, cartPrices);
      const all = Engine.compute(cfg, cartPrices.concat(units));
      const sum = units.reduce((a, u) => a + unitPrice(u), 0);
      const wasSum = units.reduce((a, u) => a + unitWas(u), 0);
      const saving = Math.max(0, all.saving - base.saving);
      return { kits: units.length, pct: all.pct, sum, saving, total: sum - saving, wasSum, sale: wasSum - sum, withCart: true };
    },
    cartItems(mainId, unitIds) {
      const lines = [{ id: Number(mainId), quantity: 1 }];
      unitIds.forEach((id) => {
        const line = lines.find((l) => l.id === Number(id));
        if (line) line.quantity += 1;
        else lines.push({ id: Number(id), quantity: 1 });
      });
      return lines;
    },
    /* "All tier % = 0" or mode none behave like none for the display */
    displayMode(cfg, n) {
      if (cfg.mode === 'none') return 'none';
      return Engine.pctFor(cfg, n) > 0 ? cfg.mode : 'none';
    }
  };

  /* ------------------------------------------------------------------ store (shared by tiers + picker) */
  class GbiBundleStore {
    constructor(root, cfg, product) {
      this.root = root;
      this.cfg = cfg;
      this.s = cfg.strings || {};
      this.sid = cfg.sectionId;
      this.product = product;
      this.tier = 1;
      this.memory = [];      // ordered extras (one entry per unit)
      this.pending = [];     // restored units waiting for their Card JSON
      this.cards = new Map();
      this.inCart = 0;
      this.cartPrices = [];  // v3.9: unit prices of kits already in the cart
      this.closed = null;    // {text, chooseTier}
      this.listeners = new Set();
      this.lastMain = null;
      if (product) this.cards.set(product.id, product);
      this.mainVariant();
      this.restore();

      this.onVariant = () => {
        if (!this.root.isConnected) return;
        this.remap();
        this.emit();
      };
      this.onCartAdded = (e) => {
        if (!this.root.isConnected) { document.removeEventListener('gbi:cart-added', this.onCartAdded); return; }
        const d = (e && e.detail) || {};
        if (d.sectionId === this.sid) this.reset();
        this.refreshCart(true);
      };
      root.addEventListener('gbi:variant-change', this.onVariant);
      document.addEventListener('gbi:cart-added', this.onCartAdded);
    }

    /* --- main kit */
    mainVariant() {
      const p = this.product;
      if (!p || !p.variants) return null;
      const raw = this.root.dataset.variantId;
      if (raw === '') return null;
      const id = raw == null ? p.selected_variant_id : Number(raw);
      const v = p.variants.find((x) => x.id === Number(id)) || null;
      if (v) this.lastMain = v;
      return v;
    }
    mainAvailable() { const v = this.mainVariant(); return !!(v && v.available); }
    mainUnit() {
      const v = this.mainVariant() || this.lastMain || (this.product && this.product.variants && this.product.variants[0]) || { id: 0, price: 0, title: '' };
      const p = this.product || {};
      return {
        variantId: v.id, productId: p.id, handle: p.handle, title: p.title || '',
        variantTitle: hasVersions(p) ? v.title : '', image: v.image || p.image || null,
        price: v.price, compareAt: cmpOf(this.cfg, v), isMain: true, followsMain: false   // 2026-10-22 sale prices: + compareAt
      };
    }
    unitFrom(json, v, source, position, followsMain) {
      return {
        key: v.id, variantId: v.id, productId: json.id, handle: json.handle, title: json.title,
        variantTitle: hasVersions(json) ? v.title : '', image: v.image || json.image || null,
        price: v.price, compareAt: cmpOf(this.cfg, v),   // 2026-10-22 sale prices: + compareAt
        source: source || 'similar', position: Number(position) || 0, followsMain: !!followsMain
      };
    }
    mainCopy(source) {
      const v = this.mainVariant();
      if (!v || !this.product) return null;
      return this.unitFrom(this.product, v, source || 'this', 0, true);
    }

    /* --- derived */
    active() { return this.tier >= 2 ? this.memory.slice(0, this.tier - 1) : []; }
    kits() { return 1 + this.active().length; }
    full() { return this.kits() >= this.tier; }
    // v3.1: Add never needs a tier switch. Only the real maximum blocks adding.
    atMax() { return this.kits() >= this.cfg.maxTier; }
    fill(n) {
      const main = this.mainUnit();
      // v3.9: the 1-kit card stays at the plain price; cart kits count from 2 kits up
      // 2026-10-22 sale prices: whole units go in (price + compareAt), so the result also has wasSum / sale
      return Engine.fill(this.cfg, main, this.memory, n, n >= 2 ? this.cartPrices : null);
    }
    savingsHidden() { return false; } // v3.9: cart kits are counted in, never hidden
    cartNote() { return this.inCart > 0 && this.cfg.mode !== 'none'; }
    state() {
      const main = this.mainUnit();
      const act = this.active();
      const r = Engine.withCart(this.cfg, [main].concat(act), this.cartPrices);
      return {
        sectionId: this.sid,
        productId: this.cfg.productId,
        tier: this.tier,
        kits: r.kits,
        complete: r.kits === this.tier,
        pct: r.pct,
        mode: this.cfg.mode,
        sum: r.sum,
        saving: r.saving,      // the bundle (BOGOS) discount only: analytics est_saving reads it
        total: r.total,
        wasSum: r.wasSum,      // 2026-10-22 sale prices: Σ compare-at (or price) of these kits
        sale: r.sale,          // 2026-10-22 sale prices: wasSum − sum, already inside the prices
        units: [main].concat(act.map((u) => ({
          variantId: u.variantId, productId: u.productId, title: u.title, variantTitle: u.variantTitle,
          image: u.image, price: u.price, compareAt: u.compareAt || null, isMain: false, followsMain: !!u.followsMain
        }))),
        cartItems: Engine.cartItems(main.variantId, act.map((u) => u.variantId))
      };
    }

    /* --- mutations */
    remap() {
      const v = this.mainVariant();
      if (!v || !this.product) return;
      if (!this.cfg.pickerEnabled) {
        this.memory = this.tier >= 2 ? Array.from({ length: this.tier - 1 }, () => this.mainCopy('this')) : [];
        return;
      }
      this.memory = this.memory.map((u) => (u.followsMain ? Object.assign(this.mainCopy(u.source), { position: u.position }) : u));
    }
    setTier(n) {
      n = Math.max(1, Math.min(this.cfg.maxTier, Number(n) || 1));
      this.tier = n;
      if (!this.cfg.pickerEnabled) {
        const v = this.mainVariant();
        this.memory = n >= 2 && v ? Array.from({ length: n - 1 }, () => this.mainCopy('this')) : [];
      }
      this.emit();
    }
    add(unit) {
      if (!unit || this.atMax()) return false;
      if (this.full()) {
        // v3.1 auto-grow: adding to a full bundle moves it up one tier (2 -> 3 -> 4).
        const from = this.tier;
        this.tier = Math.min(this.cfg.maxTier, Math.max(2, from + 1));
        this.grew = { from, to: this.tier };
        track('bundle_tier_upgrade', { from, to: this.tier, source: 'auto' });
      }
      // memory beyond the active slice is only possible while tier is lower; insert at the end of the active slice
      this.memory.splice(this.active().length, 0, unit);
      this.emit();
      return true;
    }
    removeAt(index) {
      const u = this.memory[index];
      if (!u) return null;
      this.memory.splice(index, 1);
      this.emit();
      return u;
    }
    removeLast(pred) {
      const act = this.active();
      for (let i = act.length - 1; i >= 0; i -= 1) {
        if (pred(act[i])) return this.removeAt(i);
      }
      return null;
    }
    collapse() {
      const from = this.tier;
      const filled = this.active().length;
      let closed = null;
      if (from >= 2) {
        const to = filled > 0 ? Math.min(this.cfg.maxTier, 1 + filled) : 1;
        if (to < from) {
          closed = filled > 0
            ? { text: tpl(this.s.str_closed_short, { k: 1 + filled, n: from }), chooseTier: from }
            : { text: this.s.str_closed_none || '', chooseTier: null };
          this.tier = to;
        }
      }
      this.closed = closed && closed.text ? closed : null;
      this.emit();
      return { from, filled };
    }
    clearClosed() {
      if (!this.closed) return;
      this.closed = null;
      this.notify();
    }
    reset() {
      this.tier = 1;
      this.memory = [];
      this.pending = [];
      this.closed = null;
      try { window.sessionStorage.removeItem(this.key()); } catch (_) { /* in-memory only */ }
      this.emit(true);
    }

    /* --- cards (Card JSON registry, for rehydration) */
    registerCard(json) {
      if (!json || !json.id) return;
      this.cards.set(json.id, json);
      if (this.pending.length) this.resolvePending(false);
    }
    resolvePending(final) {
      const left = [];
      let changed = false;
      this.pending.forEach((pu) => {
        const json = this.cards.get(Number(pu.productId));
        if (!json) { if (!final) left.push(pu); return; }
        let unit = null;
        if (pu.followsMain && this.product && json.id === this.product.id) {
          unit = this.mainCopy(pu.source);
        } else {
          const v = (json.variants || []).find((x) => x.id === Number(pu.variantId));
          if (v && v.available) unit = this.unitFrom(json, v, pu.source, 0, false);
        }
        if (!unit) return; // cannot be rehydrated: dropped
        unit.restoreIdx = pu.restoreIdx;
        let at = this.memory.findIndex((m) => m.restoreIdx == null || m.restoreIdx > pu.restoreIdx);
        if (at < 0) at = this.memory.length;
        this.memory.splice(at, 0, unit);
        changed = true;
      });
      this.pending = left;
      if (changed || final) this.emit();
    }

    /* --- persistence (§B4.3 Persist): no prices are ever stored */
    key() { return 'gbi-bundle-' + this.cfg.productId; }
    currency() { const g = G(); return (g.config && g.config.currency) || this.cfg.currency; }
    persist() {
      try {
        const units = this.memory.concat(this.pending).map((u) => ({
          variantId: u.variantId, productId: u.productId, handle: u.handle, source: u.source, followsMain: !!u.followsMain
        }));
        if (!units.length) window.sessionStorage.removeItem(this.key());
        else window.sessionStorage.setItem(this.key(), JSON.stringify({ currency: this.currency(), units }));
      } catch (_) { /* sessionStorage throws: in-memory only */ }
    }
    restore() {
      let data = null;
      try { data = JSON.parse(window.sessionStorage.getItem(this.key()) || 'null'); } catch (_) { data = null; }
      if (!data || !Array.isArray(data.units)) return;
      if (data.currency !== this.currency()) {
        try { window.sessionStorage.removeItem(this.key()); } catch (_) { /* ignore */ }
        return;
      }
      this.pending = data.units
        .filter((u) => u && u.variantId && u.productId)
        .slice(0, 3)
        .map((u, i) => ({ variantId: Number(u.variantId), productId: Number(u.productId), handle: u.handle, source: u.source, followsMain: !!u.followsMain, restoreIdx: i }));
      this.resolvePending(false);
    }

    /* --- kits already in the cart */
    refreshCart(force) {
      const g = G();
      if (!g.cart || typeof g.cart.get !== 'function') return Promise.resolve();
      if (this.cartChecked && !force) return this.cartPromise || Promise.resolve();
      this.cartChecked = true;
      this.cartPromise = Promise.resolve()
        .then(() => g.cart.get())
        .then((cart) => {
          // v3.9: kit units in the cart at their full unit price (gift lines and items under the
          // floor are not kits: the ≈€1 mystery item must never count as the "cheapest kit").
          const floor = Math.max(1000, Number(this.cfg.minPrice) || 0);
          const prices = [];
          ((cart && cart.items) || []).forEach((it) => {
            const unit = Number(it.original_price != null ? it.original_price : it.price) || 0;
            if (unit < floor || it.final_line_price === 0 || it.gift_card) return;
            for (let i = 0; i < (Number(it.quantity) || 0); i += 1) prices.push(unit);
          });
          const sig = prices.join(',');
          if (sig !== (this.cartPrices || []).join(',')) {
            this.cartPrices = prices;
            this.inCart = prices.length;
            this.emit(true);
          }
        })
        .catch(() => { /* /cart.js failed: no in-cart note */ });
      return this.cartPromise;
    }

    /* --- events */
    subscribe(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); }
    notify() { this.listeners.forEach((fn) => { try { fn(); } catch (err) { if (window.console) console.error(err); } }); }
    emit(skipPersist) {
      const detail = this.state();
      this.root.gbiBundle = detail;
      if (!skipPersist) this.persist();
      this.notify();
      this.root.dispatchEvent(new CustomEvent('gbi:bundle-change', { bubbles: true, detail }));
      return detail;
    }
  }

  const getStore = (root) => {
    if (!root) return null;
    if (root.gbiBundleState && root.gbiBundleState instanceof GbiBundleStore) return root.gbiBundleState;
    const cfg = readJSON(root.querySelector('[data-gbi-bundle-config]'));
    if (!cfg) return null;
    cfg.maxTier = Math.max(2, Math.min(4, Number(cfg.maxTier) || 2));
    cfg.pcts = cfg.pcts || {};
    cfg.strings = cfg.strings || {};
    cfg.excludeTypes = Array.isArray(cfg.excludeTypes) ? cfg.excludeTypes : [];
    cfg.excludeCollections = Array.isArray(cfg.excludeCollections) ? cfg.excludeCollections.map((h) => String(h).toLowerCase()) : [];
    cfg.searchEnabled = !!cfg.searchEnabled;
    cfg.limit = Number(cfg.limit) || 12;
    cfg.minPrice = Number(cfg.minPrice) || 0;
    cfg.saleOn = cfg.saleOn === true;   // 2026-10-22 sale prices: missing (older Liquid) = off
    const product = readJSON(root.querySelector('[data-gbi-product-data]'))
      || readJSON(root.querySelector('.gbi-card--this [data-gbi-product]'));
    root.gbiBundleState = new GbiBundleStore(root, cfg, product);
    return root.gbiBundleState;
  };

  /* Shared text helpers (tiers and picker use the same rules) */
  const gapText = (store) => {
    const s = store.s;
    const tier = store.tier;
    const k = tier - store.kits();
    const dm = Engine.displayMode(store.cfg, tier);
    if (dm === 'cheapest_one' && !store.savingsHidden()) {
      // 2026-10-22 sale prices: the same figure as the tier card pill (sale + bundle saving)
      const fr = store.fill(tier);
      return tpl(s.gap_save_cheapest, { k, n: tier, amount: money(fr.sale + fr.saving) });
    }
    if (dm === 'all_kits') return tpl(s.gap_save, { k, n: tier, pct: Engine.pctFor(store.cfg, tier) });
    return tpl(s.gap_plain, { k, n: tier });
  };
  const ruleText = (store, n) => {
    const s = store.s;
    const dm = Engine.displayMode(store.cfg, n);
    const pct = Engine.pctFor(store.cfg, n);
    if (dm === 'cheapest_one') return tpl(s.sub_cheapest, { pct });
    if (dm === 'all_kits') return tpl(s.sub_all, { pct });
    return s.sub_none || '';
  };
  const inCartText = (store) => {
    const s = store.s;
    const n = store.inCart;
    const c = n === 1 ? (s.kitsOne || tpl(s.kitsMany, { n })) : tpl(s.kitsMany, { n });
    return tpl(s.str_in_cart, { c, n });
  };

  /* ------------------------------------------------------------------ <gbi-bundle-tiers> */
  class GbiBundleTiers extends HTMLElement {
    connectedCallback() {
      if (this._gbiInit) return;
      this._gbiInit = true;
      this.sid = this.dataset.sectionId;
      this.root = productRootOf(this, this.sid) || this.parentElement;
      // Without <gbi-product> (should not happen) the config still lives inside this element.
      if (!this.root.querySelector('[data-gbi-bundle-config]')) this.root = this;
      this.store = getStore(this.root);
      if (!this.store) return;
      this.cfg = this.store.cfg;
      this.s = this.cfg.strings;
      this.set = this.querySelector('.gbi-bundles__set');
      this.tiers = Array.from(this.querySelectorAll('.gbi-bundles__tier'));
      this.summary = this.querySelector('[data-gbi-bundle-summary]');
      this.thumbs = this.querySelector('.gbi-bundles__thumbs');
      this.picks = this.querySelector('[data-gbi-summary-text]');
      this.action = this.querySelector('[data-gbi-summary-action]');
      this.rule = this.querySelector('[data-gbi-tier-sub]');
      this.status = this.querySelector('.gbi-bundles__status');
      this.incart = this.querySelector('[data-gbi-bundle-incart]');
      this.lastInput = '';
      this.lastKey = '';
      this.wasChecked = false;
      this.intent = false;

      this.bind();
      this.store.subscribe(() => this.render());
      this.render();
      this.gbiBundle = this.store.emit(true);
      this.editorCheck();
    }

    picker() { return this.root.querySelector('gbi-bundle-picker'); }
    open(n, trigger, opener) {
      const p = this.picker();
      if (!p || !this.cfg.pickerEnabled || typeof p.open !== 'function') return;
      p.open(n, trigger, opener);
    }

    bind() {
      const set = this.set;
      if (!set) return;
      const tierOf = (inp) => Number(inp.value) || 0;

      const onIntent = () => {
        if (this.intent) return;
        this.intent = true;
        this.store.refreshCart(false);
        const p = this.picker();
        if (p && typeof p.prefetch === 'function') p.prefetch();
      };
      set.addEventListener('pointerenter', onIntent);
      set.addEventListener('focusin', onIntent);
      set.addEventListener('touchstart', onIntent, { passive: true });

      set.addEventListener('pointerdown', (e) => {
        const lab = e.target.closest('.gbi-bundles__tier');
        if (!lab) return;
        this.lastInput = 'pointer';
        this.lastKey = '';
        const inp = lab.querySelector('.gbi-bundles__radio');
        this.wasChecked = !!(inp && inp.checked);
        this.wasCheckedFor = inp;
      });

      set.addEventListener('keydown', (e) => {
        const inp = e.target.closest('.gbi-bundles__radio');
        if (!inp) return;
        // Only keys that change or confirm the selection clear the closed status;
        // Tab/Shift must leave it, so the "Choose kits" button stays reachable.
        if (SELECT_KEYS.indexOf(e.key) !== -1) this.store.clearClosed();
        this.lastInput = 'key';
        this.lastKey = e.key;
        this.wasChecked = inp.checked;
        this.wasCheckedFor = inp;
        if (e.key === 'Enter') {
          const n = Number(inp.value);
          if (n >= 2 && this.cfg.pickerEnabled) {
            e.preventDefault();
            if (!inp.checked) {
              inp.checked = true;
              inp.dispatchEvent(new Event('change', { bubbles: true }));
            }
            this.open(n, 'keyboard', inp);
          }
        }
      });

      // Space on a radio that is already checked: Chrome fires neither click nor change,
      // so the native path never reaches us. Handle it on keyup (never on keydown).
      set.addEventListener('keyup', (e) => {
        const inp = e.target.closest('.gbi-bundles__radio');
        if (!inp || e.key !== ' ') return;
        if (this.wasChecked && this.wasCheckedFor === inp && Number(inp.value) >= 2 && this.cfg.pickerEnabled) {
          this.open(Number(inp.value), 'keyboard', inp);
        }
        this.wasChecked = false;
        this.wasCheckedFor = null;
      });

      set.addEventListener('change', (e) => {
        const inp = e.target.closest('.gbi-bundles__radio');
        if (!inp) return;
        this.store.clearClosed();
        const n = Number(inp.value);
        this.store.setTier(n);
        track('bundle_select', { tier: n, source: 'buybox', product_id: this.cfg.productId });
        if (n >= 2 && this.cfg.pickerEnabled && (this.lastInput === 'pointer' || this.lastKey === ' ')) {
          this.open(n, this.lastInput === 'pointer' ? 'card' : 'keyboard', inp);
        }
      });

      set.addEventListener('click', (e) => {
        const act = e.target.closest('[data-gbi-summary-action]');
        if (act) {
          // Read the mode and tier before clearing the closed state (that re-renders the panel).
          const mode = act.dataset.mode === 'choose' ? 'choose' : 'edit';
          const back = Number(act.dataset.tier) || (this.store.closed && this.store.closed.chooseTier) || this.store.tier;
          this.store.clearClosed();
          this.open(Math.max(2, back), mode, act);
          return;
        }
        this.store.clearClosed();
        const inp = e.target.matches && e.target.matches('.gbi-bundles__radio') ? e.target : null;
        if (!inp) return;
        const n = tierOf(inp);
        // Arrow keys fire a click on the newly checked radio: only a click on the
        // radio that was already checked when the pointer/key went down reopens.
        if (this.wasChecked && this.wasCheckedFor === inp && n >= 2 && this.cfg.pickerEnabled) {
          this.open(n, this.lastInput === 'pointer' ? 'card' : 'keyboard', inp);
        }
        this.wasChecked = false;
        this.wasCheckedFor = null;
      });
    }

    render() {
      const store = this.store;
      const cfg = this.cfg;
      const s = this.s;
      const st = store.state();
      const avail = store.mainAvailable();
      const hide = store.savingsHidden();

      if (this.set) this.set.disabled = !avail;

      this.tiers.forEach((card) => {
        const n = Number(card.dataset.tier);
        const sel = n === st.tier;
        card.classList.toggle('is-selected', sel);
        if (card.classList.contains('gbi-bundles__tier--extra')) card.hidden = !sel;
        const inp = card.querySelector('.gbi-bundles__radio');
        if (inp && inp.checked !== sel) inp.checked = sel;
        const r = store.fill(n);
        setText(card.querySelector('[data-gbi-tier-total]'), money(r.total));
        const save = card.querySelector('[data-gbi-tier-save]');
        const was = card.querySelector('[data-gbi-tier-was]');
        const showSave = n >= 2 && r.saving > 0 && Engine.displayMode(cfg, n) !== 'none' && !hide;
        // 2026-10-22 sale prices: the strike is wasSum and the pill = sale + bundle saving; the total
        // stays what the customer pays. 1 kit shows the sale only (tier_1_sale_label, empty = hidden).
        const sale = r.sale > 0 && !hide ? r.sale : 0;
        if (save) {
          if (n === 1) {
            const label = s.tier_1_sale_label || '';
            setText(save, tpl(label, { amount: money(sale) }));
            show(save, sale > 0 && !!label);
          } else {
            // Honesty rule (v2 §0.6): "Save up to" unless this card is selected and complete.
            const upto = cfg.mode === 'cheapest_one' && cfg.pickerEnabled && !(sel && st.complete && n >= 2);
            setText(save, tpl(upto ? s.saving_label_upto : s.saving_label, { amount: money(sale + (showSave ? r.saving : 0)) }));
            show(save, showSave || sale > 0);
          }
        }
        if (was) {
          setText(was, money(r.wasSum));
          show(was, showSave || sale > 0);
        }
        const pctEl = card.querySelector('[data-gbi-tier-pct]');
        if (pctEl) show(pctEl, showSave);
      });

      // Summary panel (§4.3): one message, one action, and the selected tier's rule.
      const closed = store.closed;
      let panel = false;
      let text = '';
      let mode = null;
      let actTier = 0;
      let slots = 0;
      if (closed && closed.chooseTier && avail && cfg.pickerEnabled) {
        panel = true;
        text = closed.text;
        mode = 'choose';
        actTier = closed.chooseTier;
        slots = closed.chooseTier;
      } else if (st.tier >= 2 && avail) {
        panel = true;
        slots = st.tier;
        actTier = st.tier;
        if (st.complete) {
          text = tpl(s.summary_full, { n: st.tier });
          mode = cfg.pickerEnabled ? 'edit' : null;
        } else {
          text = gapText(store);
          mode = cfg.pickerEnabled ? 'choose' : null;
        }
      }
      show(this.summary, panel);
      if (panel) {
        setText(this.picks, text);
        if (this.action) {
          const label = mode === 'choose' ? s.choose_label : s.edit_label;
          setText(this.action, label || '');
          this.action.dataset.mode = mode || 'edit';
          this.action.dataset.tier = String(actTier || '');
          show(this.action, !!mode && !!label);
        }
        this.renderThumbs(st.units, Math.max(slots, st.units.length));
        let rule = st.tier >= 2 && !hide ? ruleText(store, st.tier) : '';
        // 2026-10-22 sale prices: when the selected tier's pill includes the sale, say so, as the picker footer
        // does ("Sale price + 20% off your lowest-priced kit"; sale_rule, empty = no line).
        if (rule && store.fill(st.tier).sale > 0) rule = s.sale_rule ? tpl(s.sale_rule, { rule }) : '';
        setText(this.rule, rule);
        show(this.rule, !!rule);
      }

      // The one live region of the block (sr-only).
      setText(this.status, panel ? text : (closed ? closed.text : ''));

      // kits already in cart
      const note = store.cartNote();
      if (note) setText(this.incart, inCartText(store));
      show(this.incart, note);
    }

    renderThumbs(units, slots) {
      const ul = this.thumbs;
      if (!ul) return;
      const sig = slots + '|' + units.map((u) => u.variantId + ':' + (u.image || '')).join('|');
      if (ul.dataset.sig === sig) return;
      ul.dataset.sig = sig;
      const frag = document.createDocumentFragment();
      for (let i = 0; i < slots; i += 1) {
        const u = units[i];
        const li = document.createElement('li');
        li.className = 'gbi-bundles__thumb';
        if (u) {
          const name = u.variantTitle ? u.title + ' – ' + u.variantTitle : u.title;
          if (u.image) {
            const img = document.createElement('img');
            img.src = u.image;
            img.alt = name;
            img.width = 30; img.height = 30; img.loading = 'lazy'; img.decoding = 'async';
            li.appendChild(img);
          } else {
            const sp = document.createElement('span');
            sp.className = 'gbi-bundles__thumb-img';
            sp.setAttribute('role', 'img');
            sp.setAttribute('aria-label', name);
            li.appendChild(sp);
          }
        } else {
          li.classList.add('gbi-bundles__thumb--empty');
          li.setAttribute('aria-hidden', 'true');
          li.textContent = String(i + 1);
        }
        frag.appendChild(li);
      }
      ul.replaceChildren(frag);
    }

    /* Editor-only BOGOS check (§B4.3). Literal English is editor-only, like the gift note. */
    editorCheck() {
      const g = G();
      if (!g.config || !g.config.designMode || this.cfg.mode === 'none') return;
      const run = () => {
        if (!this.isConnected) return;
        const cfg = this.cfg;
        const fg = window.fgData;
        const label = (m) => (m === 'cheapest_one' ? 'lowest-priced kit' : m === 'all_kits' ? 'every kit' : m);
        const tiers = [2, 3, 4].filter((n) => n <= cfg.maxTier);
        let msg = '';
        if (!fg) {
          msg = "BOGOS isn't loaded here, so no bundle saving will apply.";
        } else if (fg.discounts && fg.discounts.length) {
          const d = fg.discounts.find((x) => x && x.enabled !== false) || fg.discounts[0];
          const steps = Array.isArray(d.discount) ? d.discount : [];
          const theirs = tiers.map((n) => {
            const st = steps.find((x) => Number(x.quantity) === n);
            return st && st.discount ? Number(st.discount.value) || 0 : 0;
          });
          const ours = tiers.map((n) => Number(cfg.pcts[String(n)]) || 0);
          const theirMode = d.type === 'cheapest' ? 'cheapest_one' : 'all_kits';
          if (ours.join('/') !== theirs.join('/') || theirMode !== cfg.mode) {
            msg = 'Bundle settings (' + ours.join('/') + ', ' + label(cfg.mode) + ") don't match the BOGOS discount (" + theirs.join('/') + ', ' + label(theirMode) + ').';
          }
        }
        let warn = this.querySelector('.gbi-bundles__warn');
        if (!msg) { if (warn) warn.remove(); return; }
        if (!warn) {
          warn = document.createElement('p');
          warn.className = 'gbi-bundles__warn';
          const list = this.querySelector('.gbi-bundles__list');
          if (list) list.insertAdjacentElement('afterend', warn);
          else this.appendChild(warn);
        }
        warn.textContent = msg;
      };
      const later = () => setTimeout(run, 1500);
      if (document.readyState === 'complete') later();
      else window.addEventListener('load', later, { once: true });
    }
  }

  /* ------------------------------------------------------------------ <gbi-bundle-picker> */
  class GbiBundlePicker extends HTMLElement {
    connectedCallback() {
      if (this._gbiInit) return;
      this._gbiInit = true;
      this.sid = this.dataset.sectionId;
      this.dlg = this.closest('dialog');
      this.root = productRootOf(this, this.sid);
      if (!this.root || !this.dlg) return;
      this.isOpen = false;
      this.opener = null;
      this.srcs = new Map();   // key ('rec' | 'c:<handle>' | 's:<query>') -> source state
      this.active = null;      // the source the list shows
      this.lastChip = null;    // the last chip source (search falls back to it)
      this.searchOpen = false;
      this.searchTimer = null;
      this.searchCtrl = null;
      this.restoring = null;
      this.prevKits = 1;
      this.undo = null;

      this.q = (sel) => this.querySelector(sel);
      this.el = {
        title: this.q('.gbi-picker__title'),
        body: this.q('.gbi-picker__body'),
        tiers: this.q('.gbi-picker__tiers'),
        status: this.q('[data-gbi-status-text]'),
        undo: this.q('[data-gbi-undo]'),
        fill: this.q('[data-gbi-fill]'),
        full: this.q('.gbi-picker__full'),
        fullText: this.q('[data-gbi-full-text]'),
        upgrade: this.q('[data-gbi-upgrade]'),
        thisSec: this.q('.gbi-picker__this'),
        thisCard: this.q('.gbi-card--this'),
        top: this.q('.gbi-picker__top'),
        browse: this.q('[data-gbi-browse]'),
        rail: this.q('[data-gbi-rail]'),
        searchBtn: this.q('[data-gbi-search-open]'),
        search: this.q('[data-gbi-search]'),
        searchInput: this.q('[data-gbi-search-input]'),
        searchClear: this.q('[data-gbi-search-clear]'),
        results: this.q('[data-gbi-results]'),
        more: this.q('[data-gbi-more]'),
        list: this.q('.gbi-picker__list'),
        empty: this.q('.gbi-picker__empty'),
        loaderr: this.q('.gbi-picker__loaderr'),
        incart: this.q('.gbi-picker__incart'),
        slots: this.q('.gbi-picker__slots'),
        figures: this.q('.gbi-picker__figures'),
        save: this.q('.gbi-picker__save'),
        total: this.q('.gbi-picker__total'),
        was: this.q('.gbi-picker__was'),
        rule: this.q('.gbi-picker__rule'),
        gap: this.q('.gbi-picker__gap'),
        error: this.q('.gbi-picker__error'),
        atc: this.q('.gbi-picker__atc'),
        atcLabel: this.q('.gbi-picker__atc .gbi-btn__label')
      };
      const icons = this.q('template[data-gbi-icons]');
      this.icons = {};
      if (icons && icons.content) {
        icons.content.querySelectorAll('[data-icon]').forEach((n) => { this.icons[n.dataset.icon] = n.innerHTML; });
      }
      this.upsell = this.el.list ? this.el.list.querySelector('[data-gbi-card]') : null;

      this.whenStore(() => {
        this.cfg = this.store.cfg;
        this.s = this.cfg.strings;
        [this.el.thisCard, this.upsell].forEach((li) => { if (li) this.prepareCard(li); });
        this.bind();
        this.store.subscribe(() => this.render());
        this.render();
        if (this.store.pending.length) this.restorePending();
      });
    }

    whenStore(fn) {
      this.store = getStore(this.root);
      if (this.store) { fn(); return; }
      // The tiers config renders inside the buy box, before this element; retry once the DOM is complete.
      const retry = () => { this.store = getStore(this.root); if (this.store) fn(); };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', retry, { once: true });
      else setTimeout(retry, 0);
    }

    /* --- opening */
    open(tier, trigger, opener) {
      if (!this.store) return;
      this.store.clearClosed();
      if (opener && !this.dlg.contains(opener)) this.opener = opener;
      this.store.setTier(Math.max(2, Number(tier) || 2));
      this.clearUndo();
      if (this.isOpen || this.dlg.open) { this.render(); return; }
      this.isOpen = true;
      this.hideError();
      if (this.searchOpen && this.el.searchInput && !this.el.searchInput.value.trim()) this.closeSearch(false);
      this.render();
      this.loadLists();
      this.syncTopHeight();
      const g = G();
      const opened = g.dialog && typeof g.dialog.open === 'function'
        ? g.dialog.open(this.dlg, { opener: this.opener, history: true })
        : Promise.resolve(this.dlg.showModal());
      Promise.resolve(opened).then(() => {
        if (this.el.title && this.dlg.open) this.el.title.focus({ preventScroll: true });
      }).catch(() => {});
      this.store.refreshCart(true);
      track('bundle_picker_open', { tier: this.store.tier, trigger: trigger || 'card' });
      this.root.dispatchEvent(new CustomEvent('gbi:picker-open', {
        bubbles: true, detail: { sectionId: this.sid, tier: this.store.tier, trigger: trigger || 'card', filled: this.store.active().length }
      }));
    }

    close(reason) {
      const g = G();
      if (g.dialog && typeof g.dialog.close === 'function') return g.dialog.close(this.dlg, { reason: reason || 'api' });
      if (this.dlg.open) this.dlg.close();
      this.onClosed(reason || 'api');
      return Promise.resolve();
    }

    onClosed(reason) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.clearUndo();
      const tierAtClose = this.store.tier;
      let filled = this.store.active().length;
      if (reason !== 'added') {
        const r = this.store.collapse();
        filled = r.filled;
        track('bundle_picker_close', { tier: r.from, filled: r.filled, reason });
      }
      this.root.dispatchEvent(new CustomEvent('gbi:picker-close', {
        bubbles: true, detail: { sectionId: this.sid, tier: tierAtClose, reason, filled }
      }));
      // v3.7: the dialog hands focus back to the card that opened it. When the bundle changed size
      // (auto-grow, collapse), that card is no longer the selected one and its focus ring read as a
      // second selection. Move focus to the selected card instead.
      const opener = this.opener;
      setTimeout(() => {
        const tiersEl = this.root.querySelector('gbi-bundle-tiers');
        if (!tiersEl || !tiersEl.contains(document.activeElement) || (opener && !tiersEl.contains(opener))) return;
        const sel = tiersEl.querySelector('.gbi-bundles__tier.is-selected .gbi-bundles__radio');
        if (!sel || document.activeElement === sel) return;
        try { sel.focus({ preventScroll: true, focusVisible: false }); } catch (_) { sel.focus({ preventScroll: true }); }
      }, 60);
    }

    /* Prefetch on intent: the Recommended list only (v2 §B4.2) */
    prefetch() {
      if (!this.store || !this.cfg.showSimilar || !this.dataset.similarUrl || !this.chipFor('rec')) return;
      this.run('rec').catch(() => {});
    }

    /* --- events */
    bind() {
      this.dlg.addEventListener('gbi:dialog-close', (e) => {
        if (e.target !== this.dlg) return;
        this.onClosed((e.detail && e.detail.reason) || 'close');
      });
      // Fallback when the dialog is closed without GBI.dialog (e.g. the native Esc path).
      this.dlg.addEventListener('close', () => {
        setTimeout(() => { if (this.isOpen && !this.dlg.open) this.onClosed('close'); }, 60);
      });

      if (this.el.tiers) {
        this.el.tiers.addEventListener('change', (e) => {
          const inp = e.target;
          if (!inp || inp.type !== 'radio') return;
          this.switchTier(Number(inp.value));
        });
      }
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => {
        const inp = e.target;
        if (inp && inp.matches && inp.matches('.gbi-card__val-input')) this.onVariantPick(inp);
      });

      const input = this.el.searchInput;
      if (input) {
        input.addEventListener('input', () => {
          show(this.el.searchClear, !!input.value);
          clearTimeout(this.searchTimer);
          this.searchTimer = setTimeout(() => this.runQuery(input.value), 350);
        });
        // V3M-3: while typing on a phone the footer steps aside (CSS, layout kept).
        input.addEventListener('focus', () => this.classList.add('is-typing'));
        input.addEventListener('blur', () => this.classList.remove('is-typing'));
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            clearTimeout(this.searchTimer);
            this.runQuery(input.value);
            return;
          }
          if (e.key === 'Escape' || e.key === 'Esc') {
            // Keep GBI.dialog from closing the sheet: the first Esc clears, the next closes search.
            e.preventDefault();
            e.stopPropagation();
            if (input.value) this.clearSearch();
            else this.closeSearch(true);
          }
        });
      }

      this.bindRail();

      // ≥750: the browse rail sticks right under the sticky tier band (§4.5).
      this.mqWide = window.matchMedia('(min-width: 750px)');
      const onMq = () => this.syncTopHeight();
      if (typeof this.mqWide.addEventListener === 'function') this.mqWide.addEventListener('change', onMq);
      else if (typeof this.mqWide.addListener === 'function') this.mqWide.addListener(onMq);
      if (this.el.top && typeof ResizeObserver === 'function') {
        this.topRO = new ResizeObserver(() => this.syncTopHeight());
        this.topRO.observe(this.el.top);
      }
    }

    /* V3D-3: a plain mouse can scroll the chip rail. A vertical wheel over the rail
       scrolls it sideways (until it hits an end, then the list scrolls as usual), and a
       mouse drag pans it. Touch and trackpads keep native scrolling. */
    bindRail() {
      const rail = this.el.rail;
      if (!rail || rail.dataset.gbiRailBound) return;
      rail.dataset.gbiRailBound = '1';
      rail.addEventListener('wheel', (e) => {
        if (e.ctrlKey || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
        const max = rail.scrollWidth - rail.clientWidth;
        if (max <= 1) return;
        const step = e.deltaMode === 1 ? e.deltaY * 16 : (e.deltaMode === 2 ? e.deltaY * rail.clientWidth : e.deltaY);
        const x = rail.scrollLeft;
        if ((step < 0 && x <= 0) || (step > 0 && x >= max - 1)) return;
        e.preventDefault();
        rail.scrollLeft = Math.max(0, Math.min(max, x + step));
      }, { passive: false });

      let drag = null;
      const onMove = (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        const dx = e.clientX - drag.x;
        if (!drag.moved && Math.abs(dx) < 6) return;
        drag.moved = true;
        rail.classList.add('is-dragging');
        rail.scrollLeft = drag.left - dx;
      };
      const onUp = (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        const moved = drag.moved;
        drag = null;
        rail.classList.remove('is-dragging');
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        if (moved) {
          // swallow the click that ends a drag so it does not switch chips
          const kill = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
          rail.addEventListener('click', kill, { capture: true, once: true });
          setTimeout(() => rail.removeEventListener('click', kill, { capture: true }), 0);
        }
      };
      rail.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        if (rail.scrollWidth - rail.clientWidth <= 1) return;
        drag = { id: e.pointerId, x: e.clientX, left: rail.scrollLeft, moved: false };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
      });
      rail.addEventListener('dragstart', (e) => e.preventDefault());
    }

    syncTopHeight() {
      const top = this.el.top;
      if (!top || !this.mqWide || !this.mqWide.matches) { this.style.removeProperty('--gbi-pk-top-h'); return; }
      const body = this.el.body;
      const pt = body ? parseFloat(window.getComputedStyle(body).paddingTop) || 0 : 0;
      const h = top.offsetHeight;
      if (!h) return;
      this.style.setProperty('--gbi-pk-top-h', Math.max(0, Math.round(h - pt)) + 'px');
    }

    onClick(e) {
      const t = e.target;
      const store = this.store;
      if (t.closest('[data-gbi-undo]')) { this.doUndo(); return; }
      if (t.closest('[data-gbi-fill]')) {
        const btn = t.closest('[data-gbi-fill]');
        if (isAriaDisabled(btn)) return;
        this.fillMain();
        return;
      }
      if (t.closest('[data-gbi-upgrade]')) {
        const from = store.tier;
        if (from >= this.cfg.maxTier) return;
        store.setTier(from + 1);
        track('bundle_tier_upgrade', { from, to: from + 1 });
        const next = this.el.fill && !this.el.fill.hidden ? this.el.fill : this.el.title;
        if (next) next.focus();
        return;
      }
      if (t.closest('[data-gbi-retry]')) { this.retry(t.closest('[data-gbi-retry]')); return; }
      if (t.closest('[data-gbi-search-open]')) { this.openSearch(); return; }
      if (t.closest('[data-gbi-search-close]')) { this.closeSearch(true); return; }
      if (t.closest('[data-gbi-search-clear]')) { this.clearSearch(); return; }
      if (t.closest('[data-gbi-more]')) { this.loadMore(); return; }
      const chip = t.closest('[data-gbi-rail] [data-source]');
      if (chip) {
        const key = chip.dataset.source;
        if (key !== this.active) {
          this.activate(key);
          track('bundle_picker_filter', { filter: this.trackName(key) });
          this.scrollListTop();
        }
        try { chip.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' }); } catch (_) { /* old engines */ }
        return;
      }
      if (t.closest('.gbi-picker__atc')) { this.addToCart(); return; }

      const slotX = t.closest('[data-gbi-slot-remove]');
      if (slotX) {
        const idx = Number(slotX.dataset.gbiSlotRemove);
        const u = store.active()[idx];
        if (u) {
          store.removeAt(idx);
          track('bundle_item_remove', { product_id: u.productId, variant_id: u.variantId, source: u.source, position: u.position });
          const next = this.el.slots && this.el.slots.querySelectorAll('li')[Math.min(idx + 1, store.tier - 1)];
          const f = next && next.querySelector('button');
          if (f) f.focus();
        }
        return;
      }
      if (t.closest('[data-gbi-slot-empty]')) {
        const target = isDesktop() ? (this.el.list && this.el.list.firstElementChild ? this.el.list : this.el.thisSec) : this.el.thisSec;
        if (target) target.scrollIntoView({ block: 'start', behavior: reduced() ? 'auto' : 'smooth' });
        return;
      }

      const btn = t.closest('[data-gbi-action]');
      if (!btn) return;
      const card = btn.closest('[data-gbi-card]');
      if (!card) return;
      if (isAriaDisabled(btn)) return;
      this.cardAction(card, btn.dataset.gbiAction, btn);
    }

    /* --- unit actions */
    cardJSON(card) {
      if (!card._gbiJSON) card._gbiJSON = readJSON(card.querySelector('script[data-gbi-product]')) || null;
      return card._gbiJSON;
    }
    trackAdd(u) { track('bundle_item_add', { product_id: u.productId, variant_id: u.variantId, source: u.source, position: u.position }); }
    trackRemove(u) { track('bundle_item_remove', { product_id: u.productId, variant_id: u.variantId, source: u.source, position: u.position }); }

    addMainCopy() {
      if (!this.store.mainAvailable()) return false;
      const u = this.store.mainCopy('this');
      if (u && this.store.add(u)) { this.trackAdd(u); return true; }
      return false;
    }
    fillMain() {
      const store = this.store;
      if (!store.mainAvailable()) return;
      let k = store.tier - store.kits();
      while (k > 0) {
        if (!this.addMainCopy()) break;
        k -= 1;
      }
      if (this.el.atc) this.el.atc.focus();
    }

    cardAction(card, action, btn) {
      const store = this.store;
      const json = this.cardJSON(card);
      if (!json) return;
      const isThis = card.classList.contains('gbi-card--this');
      const source = isThis ? 'this' : (card.dataset.source || 'similar');
      const position = Number(card.dataset.position) || 0;
      const multi = isMulti(json);
      card.classList.remove('is-error');

      if (isThis) {
        if (action === 'add' || action === 'more') { this.addMainCopy(); this.keepFocus(card, action); return; }
        if (action === 'less') {
          const u = store.removeLast((x) => x.productId === json.id && x.followsMain);
          if (u) this.trackRemove(u);
          this.keepFocus(card, action);
          return;
        }
        if (action === 'change') { this.togglePanel(card, btn); return; }
      } else if (!multi) {
        const v = firstAvailable(json);
        if ((action === 'add' || action === 'more') && v) {
          const u = store.unitFrom(json, v, source, position, false);
          if (store.add(u)) this.trackAdd(u);
          this.keepFocus(card, action);
          return;
        }
        if (action === 'less') {
          const u = store.removeLast((x) => x.productId === json.id);
          if (u) this.trackRemove(u);
          this.keepFocus(card, action);
          return;
        }
      } else if (action === 'add') {
        this.togglePanel(card, btn);
        return;
      }

      if (action === 'confirm') {
        const v = this.selectedVariant(card, json);
        if (!v || !v.available) return;
        const u = store.unitFrom(json, v, source, position, false);
        if (store.add(u)) this.trackAdd(u);
        return;
      }
      if (action === 'line-more') {
        const v = (json.variants || []).find((x) => x.id === Number(btn.dataset.variantId));
        if (!v || !v.available) return;
        const u = store.unitFrom(json, v, source, position, false);
        if (store.add(u)) this.trackAdd(u);
        this.keepFocus(card, action, btn.dataset.variantId);
        return;
      }
      if (action === 'line-less') {
        const vid = Number(btn.dataset.variantId);
        const u = store.removeLast((x) => x.productId === json.id && x.variantId === vid && !x.followsMain);
        if (u) this.trackRemove(u);
        this.keepFocus(card, action, btn.dataset.variantId);
      }
    }

    /* After a re-render, keep focus on the matching control in the same card. */
    keepFocus(card, action, variantId) {
      requestAnimationFrame(() => {
        if (document.activeElement && card.contains(document.activeElement) && document.activeElement.offsetParent) return;
        let el = null;
        if (variantId) el = card.querySelector('[data-gbi-action="' + action + '"][data-variant-id="' + variantId + '"]');
        if (!el || !el.offsetParent) el = card.querySelector('[data-gbi-action="' + (action === 'add' ? 'more' : action) + '"]');
        if (!el || !el.offsetParent) el = card.querySelector('.gbi-card__add');
        if (el && el.offsetParent) el.focus();
      });
    }

    /* --- variant chooser (inline disclosure) */
    togglePanel(card, btn) {
      const panel = card.querySelector('.gbi-card__variants');
      if (!panel) return;
      const json = this.cardJSON(card);
      const opening = panel.hidden;
      if (opening && !panel.dataset.built) this.buildPanel(card, json, panel);
      panel.hidden = !opening;
      card.querySelectorAll('[aria-controls="' + panel.id + '"]').forEach((b) => b.setAttribute('aria-expanded', String(opening)));
      card.classList.toggle('is-open', opening);
      if (opening) {
        this.syncPanel(card);
        const first = panel.querySelector('input:checked') || panel.querySelector('input');
        if (first && btn) first.focus({ preventScroll: true });
        this.revealPanel(panel);
        this.watchConfirm(panel);
      } else if (this.floatPanel === panel) {
        this.watchConfirm(null);
      }
    }

    /* v3.9: while a version panel is open and its Add button is not fully in view, a copy of
       that button sits at the top of the footer (same label, same state; tapping it clicks it). */
    watchConfirm(panel) {
      if (this.confirmIO) { this.confirmIO.disconnect(); this.confirmIO = null; }
      if (this.confirmMO) { this.confirmMO.disconnect(); this.confirmMO = null; }
      this.floatPanel = panel;
      this.floatVisible = true;
      const confirm = panel && panel.querySelector('.gbi-card__confirm');
      if (!confirm || !this.el.body) { this.updateFloat(); return; }
      if (!this.floatBtn) {
        const f = document.createElement('button');
        f.type = 'button';
        f.className = 'gbi-btn gbi-btn--ink gbi-btn--block gbi-picker__float';
        f.hidden = true;
        f.addEventListener('click', () => {
          const c = this.floatPanel && this.floatPanel.querySelector('.gbi-card__confirm');
          if (c && !isAriaDisabled(c) && !c.disabled) c.click();
          this.updateFloat();
        });
        const foot = this.querySelector('.gbi-picker__foot');
        if (foot) foot.insertBefore(f, foot.firstChild);
        this.floatBtn = f;
      }
      if ('IntersectionObserver' in window) {
        this.confirmIO = new IntersectionObserver((entries) => {
          entries.forEach((en) => { this.floatVisible = en.intersectionRatio >= 0.98; });
          this.updateFloat();
        }, { root: this.el.body, threshold: [0, 0.5, 0.98, 1] });
        this.confirmIO.observe(confirm);
      }
      this.confirmMO = new MutationObserver(() => this.updateFloat());
      this.confirmMO.observe(confirm, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['aria-disabled', 'disabled'] });
      this.updateFloat();
    }
    updateFloat() {
      const f = this.floatBtn;
      if (!f) return;
      const panel = this.floatPanel;
      const confirm = panel && panel.isConnected && !panel.hidden ? panel.querySelector('.gbi-card__confirm') : null;
      const show = !!confirm && !this.floatVisible;
      f.hidden = !show;
      if (!show) return;
      f.textContent = confirm.textContent;
      const off = isAriaDisabled(confirm) || confirm.disabled;
      f.disabled = !!off;
    }

    /* v3.8: after "Choose", scroll so the panel's Add button sits fully above the footer. */
    revealPanel(panel) {
      const body = this.el.body;
      const confirm = panel.querySelector('.gbi-card__confirm') || panel;
      if (!body) { panel.scrollIntoView({ block: 'nearest' }); return; }
      requestAnimationFrame(() => {
        const foot = this.querySelector('.gbi-picker__foot');
        const bodyRect = body.getBoundingClientRect();
        const limit = Math.min(bodyRect.bottom, foot ? foot.getBoundingClientRect().top : bodyRect.bottom) - 16;
        const r = confirm.getBoundingClientRect();
        let d = 0;
        if (r.bottom > limit) d = r.bottom - limit;
        const top = panel.closest('[data-gbi-card]') || panel;
        const topLimit = bodyRect.top + (this.el.browse ? this.el.browse.getBoundingClientRect().height : 0) + 8;
        if (top.getBoundingClientRect().top - d < topLimit && r.height + 32 < limit - topLimit) {
          d = Math.max(0, top.getBoundingClientRect().top - topLimit);
        }
        if (d > 0) body.scrollBy({ top: d, behavior: reduced() ? 'auto' : 'smooth' });
      });
    }

    buildPanel(card, json, panel) {
      const pid = json.id;
      const mode = card.classList.contains('gbi-card--this') ? 'this' : 'list';
      const isThis = mode === 'this';
      const start = isThis ? (this.store.mainVariant() || firstAvailable(json)) : firstAvailable(json);
      card._gbiSel = start ? start.options.slice() : (json.options || []).map((o) => o.values[0]);
      const frag = document.createDocumentFragment();
      (json.options || []).forEach((opt, i) => {
        const fs = document.createElement('fieldset');
        fs.className = 'gbi-card__opt';
        fs.dataset.index = String(i);
        const lg = document.createElement('legend');
        lg.className = 'gbi-label gbi-card__opt-name';
        lg.textContent = opt.name;
        fs.appendChild(lg);
        const vals = document.createElement('div');
        vals.className = 'gbi-card__vals';
        opt.values.forEach((val, j) => {
          const lab = document.createElement('label');
          lab.className = 'gbi-card__val';
          const inp = document.createElement('input');
          inp.type = 'radio';
          inp.className = 'gbi-sr gbi-card__val-input';
          inp.name = 'gbi-cv-' + this.sid + '-' + pid + '-' + mode + '-' + i;
          inp.value = val;
          inp.dataset.index = String(i);
          inp.id = 'GbiCv-' + this.sid + '-' + pid + '-' + mode + '-' + i + '-' + j;
          const nm = document.createElement('span');
          nm.className = 'gbi-card__val-name';
          nm.textContent = val;
          const pr = document.createElement('span');
          pr.className = 'gbi-card__val-price gbi-mono';
          pr.hidden = true;
          const so = document.createElement('span');
          so.className = 'gbi-card__val-sold';
          so.textContent = (G().strings && G().strings.soldOut) || '';
          so.hidden = true;
          lab.append(inp, nm, pr, so);
          vals.appendChild(lab);
        });
        fs.appendChild(vals);
        frag.appendChild(fs);
      });
      const confirm = document.createElement('button');
      confirm.type = 'button';
      confirm.className = 'gbi-btn gbi-btn--ink gbi-btn--block gbi-card__confirm';
      confirm.dataset.gbiAction = 'confirm';
      const h3 = card.querySelector('.gbi-card__title');
      if (h3 && h3.id) confirm.setAttribute('aria-describedby', h3.id);
      frag.appendChild(confirm);
      const lines = document.createElement('ul');
      lines.className = 'gbi-card__lines';
      lines.setAttribute('role', 'list');
      lines.setAttribute('data-gbi-list', '');
      lines.hidden = true;
      frag.appendChild(lines);
      panel.replaceChildren(frag);
      panel.dataset.built = '1';
    }

    selectedVariant(card, json) {
      const sel = card._gbiSel || [];
      return (json.variants || []).find((v) => v.options.every((o, i) => o === sel[i])) || null;
    }

    onVariantPick(inp) {
      const card = inp.closest('[data-gbi-card]');
      if (!card) return;
      const json = this.cardJSON(card);
      const i = Number(inp.dataset.index);
      card._gbiSel = (card._gbiSel || []).slice();
      card._gbiSel[i] = inp.value;
      // keep the combination available: adjust the other options to the first available match
      const v = this.selectedVariant(card, json);
      if (!v || !v.available) {
        const alt = (json.variants || []).find((x) => x.available && x.options[i] === inp.value);
        if (alt) card._gbiSel = alt.options.slice();
      }
      this.syncPanel(card);
    }

    syncPanel(card) {
      const panel = card.querySelector('.gbi-card__variants');
      if (!panel || !panel.dataset.built) return;
      const json = this.cardJSON(card);
      const store = this.store;
      const s = this.s;
      const sel = card._gbiSel || [];
      const g = G();
      panel.querySelectorAll('.gbi-card__opt').forEach((fs) => {
        const i = Number(fs.dataset.index);
        const labs = Array.from(fs.querySelectorAll('.gbi-card__val'));
        const prices = labs.map((lab) => {
          const val = lab.querySelector('input').value;
          const combo = sel.slice(); combo[i] = val;
          const exact = json.variants.find((v) => v.options.every((o, k) => o === combo[k]));
          const anyAvail = json.variants.filter((v) => v.available && v.options[i] === val);
          const price = exact && exact.available ? exact.price : (anyAvail.length ? Math.min.apply(null, anyAvail.map((v) => v.price)) : null);
          return { lab, val, exact, soldOut: !anyAvail.length || (exact && !exact.available), price };
        });
        const distinct = new Set(prices.filter((x) => x.price != null).map((x) => x.price));
        prices.forEach((x) => {
          const inp = x.lab.querySelector('input');
          inp.checked = sel[i] === x.val;
          x.lab.classList.toggle('is-selected', inp.checked);
          inp.disabled = !!x.soldOut;
          x.lab.classList.toggle('is-soldout', !!x.soldOut);
          const pr = x.lab.querySelector('.gbi-card__val-price');
          if (distinct.size > 1 && x.price != null && !x.soldOut) { setText(pr, money(x.price)); pr.hidden = false; } else pr.hidden = true;
          const so = x.lab.querySelector('.gbi-card__val-sold');
          so.hidden = !x.soldOut || !so.textContent;
        });
      });
      const v = this.selectedVariant(card, json);
      const confirm = panel.querySelector('.gbi-card__confirm');
      const full = store.atMax();
      let label;
      if (!v) label = (g.strings && g.strings.unavailable) || '';
      else if (!v.available) label = (g.strings && g.strings.soldOut) || '';
      else label = tpl(s.str_confirm, { price: money(v.price) });
      setText(confirm, label);
      ariaDisable(confirm, !v || !v.available || full);

      // added lines: one row per chosen variant
      const isThis = card.classList.contains('gbi-card--this');
      const units = store.active().filter((u) => u.productId === json.id && (!isThis || !u.followsMain));
      const groups = [];
      units.forEach((u) => {
        const gr = groups.find((x) => x.variantId === u.variantId);
        if (gr) gr.n += 1; else groups.push({ variantId: u.variantId, n: 1, label: u.variantTitle || u.title });
      });
      const lines = panel.querySelector('.gbi-card__lines');
      const title = card.dataset.fullTitle || json.title;
      const sig = groups.map((x) => x.variantId + 'x' + x.n).join('|') + (full ? 'F' : '');
      if (lines.dataset.sig !== sig) {
        lines.dataset.sig = sig;
        const frag = document.createDocumentFragment();
        groups.forEach((gr) => {
          const li = document.createElement('li');
          li.className = 'gbi-card__line';
          const nm = document.createElement('span');
          nm.className = 'gbi-card__line-name';
          nm.textContent = gr.label.split(' / ').join(' · ');
          const step = document.createElement('div');
          step.className = 'gbi-card__stepper gbi-card__stepper--line';
          const less = this.stepBtn('line-less', gr.variantId, tpl(s.str_less, { title: title + ' – ' + gr.label }), 'minus');
          const out = document.createElement('output');
          out.className = 'gbi-card__qty gbi-mono';
          out.textContent = String(gr.n);
          const more = this.stepBtn('line-more', gr.variantId, tpl(s.str_more, { title: title + ' – ' + gr.label }), 'plus');
          ariaDisable(more, full);
          step.append(less, out, more);
          li.append(nm, step);
          frag.appendChild(li);
        });
        lines.replaceChildren(frag);
      }
      lines.hidden = groups.length === 0;
    }

    stepBtn(action, variantId, aria, icon) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'gbi-card__step';
      b.dataset.gbiAction = action;
      b.dataset.variantId = String(variantId);
      b.setAttribute('aria-label', aria);
      b.innerHTML = this.icons[icon] || '';
      return b;
    }

    /* --- card preparation (labels set before the card is shown, §B4.4) */
    prepareCard(li) {
      if (li._gbiPrepared) return;
      li._gbiPrepared = true;
      const s = this.s;
      const json = this.cardJSON(li);
      if (!json) return;
      this.store.registerCard(json);
      const isThis = li.classList.contains('gbi-card--this');
      const multi = isMulti(json);
      const title = li.dataset.fullTitle || json.title;
      const add = li.querySelector('.gbi-card__add');
      const panel = li.querySelector('.gbi-card__variants');
      if (add) {
        if (isThis) add.textContent = s.str_add_another || '';
        else if (multi) {
          add.textContent = s.str_choose_version || '';
          if (panel) { add.setAttribute('aria-expanded', 'false'); add.setAttribute('aria-controls', panel.id); }
        } else add.textContent = s.str_add || '';
      }
      const less = li.querySelector('.gbi-card__stepper [data-gbi-action="less"]');
      const more = li.querySelector('.gbi-card__stepper [data-gbi-action="more"]');
      if (less) less.setAttribute('aria-label', tpl(s.str_less, { title }));
      if (more) more.setAttribute('aria-label', tpl(s.str_more, { title }));
      if (less && !less.innerHTML.trim()) less.innerHTML = this.icons.minus || '';
      if (more && !more.innerHTML.trim()) more.innerHTML = this.icons.plus || '';

      // "Change" link on a multi-variant This-kit card
      if (isThis && multi && panel && s.str_change) {
        const meta = li.querySelector('.gbi-card__meta');
        if (meta && !meta.querySelector('.gbi-card__change')) {
          const ch = document.createElement('button');
          ch.type = 'button';
          ch.className = 'gbi-link gbi-card__change';
          ch.dataset.gbiAction = 'change';
          ch.textContent = s.str_change;
          ch.setAttribute('aria-expanded', 'false');
          ch.setAttribute('aria-controls', panel.id);
          const h3 = li.querySelector('.gbi-card__title');
          if (h3 && h3.id) ch.setAttribute('aria-describedby', h3.id);
          meta.appendChild(ch);
        }
      }

      // difficulty meter aria (empty when rendered by gbi-bundle-items)
      const f = json.facts || {};
      li.querySelectorAll('[aria-label=""]').forEach((m) => {
        if (m.matches('[data-gbi-action]')) return;
        if (f.level && s.str_difficulty_aria) m.setAttribute('aria-label', tpl(s.str_difficulty_aria, { label: f.difficulty, x: f.level, n: f.levels || 4 }));
        else if (f.difficulty) m.setAttribute('aria-label', f.difficulty);
      });

      // price: "from [price]" when the price varies
      // The words around [price] set small, so "from €243,95" stays on one line (PICK-5).
      const price = li.querySelector('[data-gbi-card-price]');
      if (price && !isThis && json.price_varies && s.str_from) {
        const parts = String(s.str_from).split('[price]');
        const frag = document.createDocumentFragment();
        parts.forEach((part, i) => {
          if (i > 0) frag.appendChild(document.createTextNode(money(minAvailablePrice(json))));
          const txt = part.trim();
          if (txt) {
            const sp = document.createElement('span');
            sp.className = 'gbi-card__from';
            sp.textContent = txt;
            frag.appendChild(sp);
          }
        });
        price.replaceChildren(frag);
      }
      // 2026-10-22 sale prices: a list card strikes the compare-at of the variant behind its price
      // (the cheapest available one: the "from" price, or the one price when it does not vary).
      if (price && !isThis) {
        const sv = cheapestAvailable(json);
        const shown = json.price_varies && s.str_from
          ? minAvailablePrice(json)
          : Math.min.apply(null, (json.variants || []).map((x) => x.price));
        this.cardWas(li, sv && sv.price === shown ? cmpOf(this.cfg, sv) : null);
      }

      // "Added" badge
      const badge = li.querySelector('[data-gbi-card-badge]');
      if (badge && s.str_added) badge.innerHTML = (this.icons.check || '') + '<span></span>';
      if (badge && badge.lastElementChild) badge.lastElementChild.textContent = s.str_added || '';
    }

    /* 2026-10-22 sale prices: a small struck compare-at after a card price, with the locale's
       "Sale price" / "Regular price" for screen readers. Created when needed and removed again
       (cmp null), so a card without a sale stays exactly as before. Mirrors gbi-bundle-card.liquid. */
    cardWas(li, cmp) {
      const price = li.querySelector('[data-gbi-card-price]');
      if (!price) return;
      let sr = li.querySelector('[data-gbi-card-sr]');
      let was = li.querySelector('[data-gbi-card-was]');
      if (!cmp) {
        if (sr) sr.remove();
        if (was) was.remove();
        return;
      }
      const s = this.s;
      if (!sr && s.str_sale_price) {
        sr = document.createElement('span');
        sr.className = 'gbi-sr';
        sr.setAttribute('data-gbi-card-sr', '');
        sr.textContent = s.str_sale_price;
        price.before(sr);
      }
      if (!was) {
        was = document.createElement('s');
        was.className = 'gbi-card__was gbi-mono';
        was.setAttribute('data-gbi-card-was', '');
        price.after(was);
      }
      const label = s.str_regular_price || '';
      const text = money(cmp);
      const sig = label + '|' + text;
      if (was.dataset.sig === sig) return;
      was.dataset.sig = sig;
      const frag = document.createDocumentFragment();
      if (label) {
        const l = document.createElement('span');
        l.className = 'gbi-sr';
        l.textContent = label;
        frag.appendChild(l);
      }
      frag.appendChild(document.createTextNode(text));
      was.replaceChildren(frag);
    }

    syncCard(li) {
      const json = this.cardJSON(li);
      if (!json) return;
      const store = this.store;
      const s = this.s;
      const isThis = li.classList.contains('gbi-card--this');
      const multi = isMulti(json);
      const full = store.atMax();
      const act = store.active();
      const add = li.querySelector('.gbi-card__add');
      const stepper = li.querySelector('.gbi-card__actions > .gbi-card__stepper');
      const qty = stepper && stepper.querySelector('.gbi-card__qty');
      const more = stepper && stepper.querySelector('[data-gbi-action="more"]');
      let count = 0;
      let added = false;

      if (isThis) {
        const v = store.mainVariant();
        const price = li.querySelector('[data-gbi-card-price]');
        if (v && price) setText(price, money(v.price));
        if (v) this.cardWas(li, cmpOf(this.cfg, v));   // 2026-10-22 sale prices: follows the chosen version
        const mainOk = store.mainAvailable();
        count = act.filter((u) => u.productId === json.id && u.followsMain).length;
        added = act.some((u) => u.productId === json.id);
        show(add, count === 0);
        ariaDisable(add, full || !mainOk);
        show(stepper, count > 0);
        if (qty) setText(qty, String(1 + count));
        ariaDisable(more, full || !mainOk);
        const ch = li.querySelector('.gbi-card__change');
        if (ch) show(ch, mainOk || !!store.product);
      } else if (!multi) {
        count = act.filter((u) => u.productId === json.id).length;
        added = count > 0;
        const v = firstAvailable(json);
        show(add, count === 0);
        ariaDisable(add, full || !v);
        show(stepper, count > 0);
        if (qty) setText(qty, String(count));
        ariaDisable(more, full || !v);
      } else {
        count = act.filter((u) => u.productId === json.id).length;
        added = count > 0;
        show(add, true);
        ariaDisable(add, full && !li.querySelector('.gbi-card__panel:not([hidden])'));
        show(stepper, false);
      }
      li.classList.toggle('is-added', added);
      const badge = li.querySelector('[data-gbi-card-badge]');
      if (badge) show(badge, added && !!s.str_added);
      this.syncPanel(li);
    }

    /* --- lists (§4.6): Recommended, collection chips and search, all through the
       Section Rendering API (sections/gbi-bundle-items). One source state per key:
       {key, cards, page, next, done, loading, error, seen, tracked}. */
    chipFor(key) {
      if (!this.el.rail) return null;
      return Array.from(this.el.rail.querySelectorAll('[data-source]')).find((b) => b.dataset.source === key) || null;
    }
    chipKeys() {
      if (!this.el.rail) return [];
      return Array.from(this.el.rail.querySelectorAll('[data-source]'))
        .filter((b) => !b.hidden && (b.dataset.source !== 'rec' || this.dataset.similarUrl))
        .map((b) => b.dataset.source);
    }
    isSearch(key) { return !!key && key.indexOf('s:') === 0; }
    trackName(key) {
      if (key === 'rec') return 'recommended';
      if (this.isSearch(key)) return 'search';
      return 'collection:' + String(key || '').slice(2);
    }

    getSource(key) {
      let S = this.srcs.get(key);
      if (S) {
        if (this.isSearch(key)) { this.srcs.delete(key); this.srcs.set(key, S); }   // LRU touch
        return S;
      }
      S = { key, cards: [], page: 0, next: null, done: false, loading: null, error: false, seen: new Set(), tracked: false };
      if (this.store.product) S.seen.add(Number(this.store.product.id));
      S.seen.add(Number(this.cfg.productId));
      if (this.upsell) S.seen.add(Number(this.upsell.dataset.productId));
      this.srcs.set(key, S);
      this.evict();
      return S;
    }
    evict() {
      const keep = (k) => k === this.active || k === this.lastChip || k === 'rec';
      const searches = Array.from(this.srcs.keys()).filter((k) => this.isSearch(k));
      while (searches.length > 8) {
        const k = searches.shift();
        if (!keep(k)) this.srcs.delete(k);
      }
      const all = Array.from(this.srcs.keys());
      for (let i = 0; this.srcs.size > 20 && i < all.length; i += 1) {
        if (!keep(all[i])) this.srcs.delete(all[i]);
      }
    }

    sourceUrl(key, page) {
      if (key === 'rec') return this.dataset.similarUrl || '';
      if (this.isSearch(key)) {
        const base = this.el.searchInput && this.el.searchInput.dataset.url;
        return base ? base + '&q=' + encodeURIComponent(key.slice(2)) + '&page=' + page : '';
      }
      const chip = this.chipFor(key);
      return chip && chip.dataset.url ? chip.dataset.url + '&page=' + page : '';
    }

    /* Keep a card only when it can join the bundle (v2 rules + excludeCollections). */
    accept(S, nodes) {
      const ex = this.cfg.excludeTypes.map((x) => String(x).toLowerCase());
      const exC = this.cfg.excludeCollections;
      let added = 0;
      nodes.forEach((node) => {
        const json = readJSON(node.querySelector('script[data-gbi-product]'));
        if (!json || S.seen.has(Number(json.id))) return;
        if (json.type && ex.indexOf(String(json.type).toLowerCase()) !== -1) return;
        if (this.cfg.minPrice > 0 && minAvailablePrice(json) < this.cfg.minPrice) return;
        if (!firstAvailable(json)) return;
        if (exC.length && Array.isArray(json.collections) && json.collections.some((h) => exC.indexOf(String(h).toLowerCase()) !== -1)) return;
        S.seen.add(Number(json.id));
        const li = document.importNode(node, true);
        li._gbiJSON = json;
        li.dataset.source = S.key === 'rec' ? 'similar' : (this.isSearch(S.key) ? 'search' : 'collection');
        li.dataset.position = String(S.cards.length + 1);
        this.prepareCard(li);
        S.cards.push(li);
        added += 1;
      });
      if (S.key === 'rec' && S.cards.length > this.cfg.limit) S.cards = S.cards.slice(0, this.cfg.limit);
      return added;
    }

    fetchHTML(url, signal) {
      const opts = { credentials: 'same-origin', headers: { Accept: 'text/html' } };
      if (signal) opts.signal = signal;
      return fetch(String(url), opts)
        .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.text(); })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const ul = doc.querySelector('[data-gbi-bundle-items]');
          return {
            nodes: ul ? Array.from(ul.children).filter((n) => n.matches('li')) : [],
            next: ul && ul.dataset.next ? Number(ul.dataset.next) || null : null
          };
        });
    }

    fetchOne(S) {
      const page = S.page + 1;
      const url = this.sourceUrl(S.key, page);
      if (!url) { S.done = true; S.next = null; return Promise.resolve(0); }
      let signal = null;
      if (this.isSearch(S.key) && typeof AbortController === 'function') {
        if (this.searchCtrl) this.searchCtrl.abort();
        this.searchCtrl = new AbortController();
        signal = this.searchCtrl.signal;
      }
      return this.fetchHTML(url, signal).then((res) => {
        const added = this.accept(S, res.nodes);
        S.page = page;
        S.next = S.key === 'rec' ? null : res.next;
        S.done = !S.next;
        return added;
      });
    }

    /* Fetch the next page. A page that leaves fewer than 4 new cards continues at
       once (at most 2 extra pages), so a page of accessories never shows as empty. */
    run(key) {
      const S = this.getSource(key);
      if (S.loading) return S.loading;
      if (S.done) return Promise.resolve(0);
      let added = 0;
      let extra = 0;
      const step = () => this.fetchOne(S).then((n) => {
        added += n;
        if (!S.done && added < 4 && extra < 2) { extra += 1; return step(); }
        return added;
      });
      S.error = false;
      S.loading = step()
        .catch((err) => {
          if (!(err && err.name === 'AbortError')) S.error = true;
          throw err;
        })
        .finally(() => { S.loading = null; });
      return S.loading;
    }

    activate(key, auto) {
      this.active = key;
      if (key && !this.isSearch(key)) this.lastChip = key;
      if (!key) { this.renderList(); return; }
      const S = this.getSource(key);
      if (!S.cards.length && !S.done && !S.loading && !S.error) {
        this.run(key).catch(() => {}).then(() => this.afterLoad(key, auto));
      } else if (S.loading) {
        S.loading.catch(() => {}).then(() => this.afterLoad(key, auto));
      }
      this.renderList();
    }

    afterLoad(key, auto) {
      const S = this.srcs.get(key);
      if (!S) return;
      // Recommended came back empty: hide its chip and move on to the next one.
      if (key === 'rec' && S.done && !S.error && !S.cards.length) {
        const chip = this.chipFor('rec');
        if (chip) chip.hidden = true;
        if (this.active === 'rec') {
          const next = this.chipKeys()[0] || null;
          this.activate(next, auto);
          return;
        }
      }
      if (this.isSearch(key) && !S.tracked && !S.loading && !S.error) {
        S.tracked = true;
        track('bundle_picker_search', { chars: key.length - 2, has_results: S.cards.length > 0 });
      }
      this.renderList();
      if (key === this.active && !S.error) this.restorePending();
    }

    loadLists() {
      if (!this.active || (!this.isSearch(this.active) && this.chipKeys().indexOf(this.active) === -1)) {
        this.active = this.chipKeys()[0] || null;
      }
      if (!this.active) { this.renderList(); this.restorePending(); return; }
      const S = this.getSource(this.active);
      if (S.error) S.error = false;
      this.activate(this.active, true);
    }

    loadMore() {
      const key = this.active;
      const S = key && this.srcs.get(key);
      if (!S || S.done || S.loading) return;
      const before = S.cards.length;
      track('bundle_picker_more', { source: this.trackName(key), page: S.page + 1 });
      const p = this.run(key);
      this.renderList();
      p.catch(() => {}).then(() => {
        if (this.active !== key) return;
        this.renderList();
        const first = S.cards[before];
        const ae = document.activeElement;
        const onMore = !ae || ae === document.body || ae === this.el.more;
        if (first && onMore) {
          const btn = Array.from(first.querySelectorAll('[data-gbi-action]')).find((b) => b.offsetParent);
          if (btn) btn.focus();
        }
      });
    }

    /* PICK-6: after a successful retry, focus moves into the list, not to <body>. */
    retry(btn) {
      const key = this.active;
      const hadFocus = !!btn && document.activeElement === btn;
      if (!key) { this.loadLists(); return; }
      const S = this.getSource(key);
      S.error = false;
      const p = this.run(key);
      this.renderList();
      p.catch(() => {}).then(() => {
        this.afterLoad(key, false);
        if (!hadFocus || S.error) return;
        const target = this.el.list && Array.from(this.el.list.querySelectorAll('[data-gbi-action]')).find((b) => b.offsetParent);
        if (target) target.focus();
        else if (this.el.list) {
          if (!this.el.list.hasAttribute('tabindex')) this.el.list.setAttribute('tabindex', '-1');
          this.el.list.focus();
        }
      });
    }

    /* After a chip switch, show the new list from its start (under the sticky rail). */
    scrollListTop() {
      const body = this.el.body;
      const list = this.el.list;
      const browse = this.el.browse;
      if (!body || !list || !browse) return;
      const d = list.getBoundingClientRect().top - browse.getBoundingClientRect().bottom;
      if (d < 0) body.scrollTop += d;
    }

    /* --- search (§4.6) */
    openSearch() {
      if (!this.el.search || !this.el.searchInput) return;
      this.searchOpen = true;
      show(this.el.rail, false);
      show(this.el.search, true);
      if (this.el.searchBtn) { this.el.searchBtn.setAttribute('aria-expanded', 'true'); show(this.el.searchBtn, false); }
      this.classList.add('is-searching');
      this.el.searchInput.focus({ preventScroll: true });
      this.searchToTop();
    }
    /* V3M-3: below 750 the search field moves to the top of the sheet body, so the
       results get the room above the on-screen keyboard. */
    searchToTop() {
      const body = this.el.body;
      const browse = this.el.browse;
      if (!body || !browse || isWide()) return;
      requestAnimationFrame(() => {
        const pt = parseFloat(window.getComputedStyle(body).paddingTop) || 0;
        const d = browse.getBoundingClientRect().top - body.getBoundingClientRect().top - pt;
        if (d > 1) body.scrollTop += d;
      });
    }
    closeSearch(focusBtn) {
      if (!this.el.search) return;
      clearTimeout(this.searchTimer);
      if (this.searchCtrl) { this.searchCtrl.abort(); this.searchCtrl = null; }
      this.searchOpen = false;
      this.classList.remove('is-searching', 'is-typing');
      if (this.el.searchInput) this.el.searchInput.value = '';
      show(this.el.searchClear, false);
      show(this.el.search, false);
      show(this.el.rail, true);
      if (this.el.searchBtn) { this.el.searchBtn.setAttribute('aria-expanded', 'false'); show(this.el.searchBtn, true); }
      if (this.isSearch(this.active) || !this.active) this.activate(this.lastChip || this.chipKeys()[0] || null);
      else this.renderList();
      if (focusBtn && this.el.searchBtn) this.el.searchBtn.focus();
    }
    clearSearch() {
      clearTimeout(this.searchTimer);
      if (this.searchCtrl) { this.searchCtrl.abort(); this.searchCtrl = null; }
      if (this.el.searchInput) this.el.searchInput.value = '';
      show(this.el.searchClear, false);
      if (this.isSearch(this.active)) this.activate(this.lastChip || this.chipKeys()[0] || null);
      else this.renderList();
      if (this.el.searchInput) this.el.searchInput.focus();
    }
    runQuery(raw) {
      const q = String(raw || '').trim().replace(/\s+/g, ' ');
      if (q.length < 2) {
        if (this.isSearch(this.active)) this.activate(this.lastChip || this.chipKeys()[0] || null);
        return;
      }
      const key = 's:' + q.toLowerCase();
      if (key === this.active) return;
      this.activate(key);
    }
    searchMatches(S) {
      if (S._matchN === S.cards.length) return S._match;
      const words = S.key.slice(2).toLowerCase().split(/[^0-9a-z\u00c0-\u024f]+/).filter((w) => w.length >= 2);
      const hit = !words.length || S.cards.some((li) => {
        const j = li._gbiJSON || {};
        const hay = [j.title, j.type, j.handle].concat(Array.isArray(j.tags) ? j.tags : [], Array.isArray(j.collections) ? j.collections : [])
          .join(' ').toLowerCase();
        return words.some((w) => hay.indexOf(w) !== -1);
      });
      S._matchN = S.cards.length;
      S._match = hit;
      return hit;
    }
    renderResults(S) {
      const el = this.el.results;
      if (!el) return;
      const s = this.s;
      let tplStr = '';
      if (S && this.isSearch(S.key) && !(S.loading && !S.cards.length) && !S.error) {
        // V3M-4: the store search is fuzzy, so a result count is no proof of a match.
        // When no card names any word of the query, say so; the cards stay as ideas.
        if (S.cards.length) tplStr = this.searchMatches(S) ? (s.str_results || '') : (s.str_no_results || '');
        else if (S.done) tplStr = s.str_no_results || '';
      }
      const q = S ? S.key.slice(2) : '';
      const sig = tplStr ? tplStr + '|' + q : '';
      if (el.dataset.sig !== sig) {
        el.dataset.sig = sig;
        const frag = document.createDocumentFragment();
        if (tplStr) {
          const typed = (this.el.searchInput && this.el.searchInput.value.trim()) || q;
          String(tplStr).split('[q]').forEach((part, i) => {
            if (i > 0) {
              const b = document.createElement('span');
              b.className = 'gbi-picker__results-q';
              b.textContent = typed;
              frag.appendChild(b);
            }
            if (part) frag.appendChild(document.createTextNode(part));
          });
        }
        el.replaceChildren(frag);
      }
      show(el, !!tplStr);
    }

    /* Session restore (v2 §B4.3 Persist) for kits from any list or search: fetch
       each pending product's card from /products/<handle>?section_id=gbi-bundle-items.
       Once every fetch has answered, what is still pending is dropped (v2 rule). */
    restorePending() {
      const store = this.store;
      if (!store || !store.pending.length) return;
      if (this.restoring) return;
      const rootUrl = String(((G().config || {}).routes || {}).root || '/').replace(/\/$/, '');
      const handles = [];
      store.pending.forEach((pu) => {
        if (!store.cards.get(Number(pu.productId)) && pu.handle && handles.indexOf(pu.handle) === -1) handles.push(pu.handle);
      });
      let failed = false;
      this.restoring = Promise.all(handles.map((h) => this.fetchHTML(rootUrl + '/products/' + encodeURIComponent(h) + '?section_id=gbi-bundle-items')
        .then((res) => {
          res.nodes.forEach((node) => {
            const json = readJSON(node.querySelector('script[data-gbi-product]'));
            if (json) store.registerCard(json);
          });
        })
        .catch(() => { failed = true; })))
        .then(() => {
          this.restoring = null;
          // Every product answered: what is still pending can't join the bundle any more.
          if (!failed && store.pending.length) store.resolvePending(true);
        });
    }

    renderList() {
      const list = this.el.list;
      if (!list) return;
      const key = this.active;
      const S = key ? this.srcs.get(key) : null;
      const search = this.isSearch(key);
      const cards = S ? S.cards : [];
      const loadingFirst = !!(S && S.loading && !cards.length);
      const failed = !!(S && S.error);
      const nodes = [];
      if (this.upsell && !search) nodes.push(this.upsell);
      const kids = Array.from(list.children);
      let changed;
      if (loadingFirst) {
        const skels = kids.filter((c) => c.classList.contains('gbi-card--skel'));
        changed = !(skels.length === 4 && kids.length === 4 + nodes.length && (!nodes.length || kids[0] === nodes[0]));
        if (changed) {
          for (let i = 0; i < 4; i += 1) {
            const sk = document.createElement('li');
            sk.className = 'gbi-card gbi-card--skel';
            sk.setAttribute('aria-hidden', 'true');
            sk.innerHTML = '<span class="gbi-skel gbi-card__skel-media"></span><span class="gbi-card__skel-body"><span class="gbi-skel gbi-card__skel-line"></span><span class="gbi-skel gbi-card__skel-line gbi-card__skel-line--short"></span></span>';
            nodes.push(sk);
          }
        } else {
          skels.forEach((sk) => nodes.push(sk));
        }
      } else {
        cards.forEach((li) => nodes.push(li));
        changed = !(kids.length === nodes.length && nodes.every((n, i) => kids[i] === n));
      }
      // Only touch the DOM when the list really changes: replacing the node that
      // holds focus would drop keyboard focus to <body> (e.g. on a background fetch).
      if (changed) {
        const ae = document.activeElement;
        const hadFocus = !!(ae && ae !== document.body && list.contains(ae));
        list.replaceChildren.apply(list, nodes);
        if (hadFocus && ae.isConnected && document.activeElement !== ae) {
          try { ae.focus({ preventScroll: true }); } catch (err) { /* ignore */ }
        }
      }
      list.setAttribute('aria-busy', S && S.loading ? 'true' : 'false');
      nodes.forEach((li) => { if (li.matches('[data-gbi-card]')) this.syncCard(li); });

      // Load more: the active source has another page.
      if (this.el.more) {
        const more = !!(S && !S.done && !failed && !loadingFirst && S.page > 0);
        show(this.el.more, more);
        if (S && S.loading && more) this.el.more.setAttribute('aria-busy', 'true');
        else this.el.more.removeAttribute('aria-busy');
      }
      show(this.el.loaderr, failed);
      const settled = !S || (!S.loading && S.done);
      show(this.el.empty, !search && !loadingFirst && !failed && settled && cards.length === 0 && !this.upsell);
      this.renderResults(search ? S : null);

      // chips: pressed = the list on show (or the one search falls back to)
      if (this.el.rail) {
        const pressedKey = search ? this.lastChip : key;
        this.el.rail.querySelectorAll('[data-source]').forEach((b) => {
          b.setAttribute('aria-pressed', String(b.dataset.source === pressedKey));
        });
      }
    }

    /* --- tier switch inside the picker (§B4.3) */
    switchTier(n) {
      const store = this.store;
      n = Math.max(2, Math.min(this.cfg.maxTier, n));
      if (n === store.tier) return;
      const act = store.active();
      const prev = { memory: store.memory.slice(), tier: store.tier };
      track('bundle_select', { tier: n, source: 'picker', product_id: this.cfg.productId });
      if (act.length > n - 1) {
        const dropped = act.slice(n - 1);
        store.memory = store.memory.filter((u) => dropped.indexOf(u) === -1);
        store.setTier(n);
        dropped.forEach((u) => this.trackRemove(u));
        const titles = dropped.map((u) => cleanTitle(u.title)).filter((x, i, a) => a.indexOf(x) === i).join(', ');
        this.showUndo(tpl(this.s.str_removed, { title: titles, n }), prev);
      } else {
        this.clearUndo();
        store.setTier(n);
      }
    }

    showUndo(text, prev) {
      this.clearUndo();
      this.undo = { text, prev, timer: setTimeout(() => this.clearUndo(true), 5000) };
      this.render();
    }
    clearUndo(rerender) {
      if (!this.undo) return;
      clearTimeout(this.undo.timer);
      const hadFocus = this.el.undo && document.activeElement === this.el.undo;
      this.undo = null;
      if (rerender) {
        this.render();
        if (hadFocus && this.el.title) this.el.title.focus();
      }
    }
    doUndo() {
      if (!this.undo) return;
      const prev = this.undo.prev;
      this.clearUndo();
      this.store.memory = prev.memory;
      this.store.setTier(prev.tier);
      if (this.el.title) this.el.title.focus();
    }

    /* --- render */
    render() {
      if (!this.store) return;
      const store = this.store;
      const cfg = this.cfg;
      const s = this.s;
      const st = store.state();
      const tier = Math.max(2, st.tier);
      const kits = st.kits;
      const full = kits >= tier;
      const mainOk = store.mainAvailable();
      const hide = store.savingsHidden();

      setText(this.el.title, tpl(s.picker_title, { n: tier }));
      if (this.el.tiers) {
        this.el.tiers.querySelectorAll('input[type="radio"]').forEach((inp) => {
          const on = Number(inp.value) === tier;
          if (inp.checked !== on) inp.checked = on;
          const lab = inp.closest('.gbi-picker__seg');
          if (lab) lab.classList.toggle('is-selected', on);
          if (lab) {
            const reached = kits >= Number(inp.value);
            const was = lab.classList.contains('is-reached');
            lab.classList.toggle('is-reached', reached);
            if (reached && !was && this.isOpen && this.prevKits != null && kits > this.prevKits && !reduced()) {
              lab.classList.remove('is-celebrating');
              void lab.offsetWidth;
              lab.classList.add('is-celebrating');
              setTimeout(() => lab.classList.remove('is-celebrating'), 900);
            }
          }
          const segSave = lab && lab.querySelector('[data-gbi-seg-save]');
          if (segSave) {
            const n = Number(inp.value);
            const fr = store.fill(n);
            // 2026-10-22 sale prices: amount = sale + bundle saving, the same figure as the tier card pill
            const bundleOk = fr.saving > 0 && !hide && Engine.displayMode(cfg, n) !== 'none';
            const amount = (fr.sale > 0 && !hide ? fr.sale : 0) + (bundleOk ? fr.saving : 0);
            const ok = amount > 0;
            // v3.9: two rows, "SAVE UP TO" over the amount (split the setting at [amount])
            const parts = String(s.seg_save || '').split('[amount]');
            const pre = (parts[0] || '').trim();
            const amt = money(amount) + (parts[1] || '');
            const sig = ok ? pre + '|' + amt : '';
            if (segSave.dataset.sig !== sig) {
              segSave.dataset.sig = sig;
              segSave.textContent = '';
              if (ok) {
                if (pre) { const a = document.createElement('span'); a.className = 'gbi-picker__seg-save-pre'; a.textContent = pre; segSave.appendChild(a); }
                const b = document.createElement('span'); b.className = 'gbi-picker__seg-save-amt'; b.textContent = amt.trim(); segSave.appendChild(b);
              }
            }
            show(segSave, ok);
          }
          if (lab && on && store.grew && store.grew.to === tier && !reduced()) {
            lab.classList.remove('is-bumped');
            void lab.offsetWidth;
            lab.classList.add('is-bumped');
          }
        });
        store.grew = null;
        // v3.7 progress line: filled from the first milestone (2 kits) to the kits chosen
        const span = Math.max(1, cfg.maxTier - 2);
        const prog = Math.max(0, Math.min(1, (kits - 2) / span));
        this.el.tiers.style.setProperty('--gbi-pk-progress', String(prog));
        this.el.tiers.classList.toggle('has-kits', kits >= 2);
      }

      // status (or the "Removed … Undo" message)
      if (this.undo) {
        setText(this.el.status, this.undo.text);
        show(this.el.undo, true);
      } else {
        setText(this.el.status, full ? tpl(s.summary_full, { n: tier }) : tpl(s.summary_text, { x: kits, n: tier }));
        show(this.el.undo, false);
      }
      const statusP = this.el.status && this.el.status.parentElement;
      if (statusP) {
        statusP.classList.toggle('has-undo', !!this.undo);
        statusP.classList.toggle('is-full', full);
      }

      // fill with this kit: only with 2+ open slots; with one left, "Add another" does it (MOB-7)
      const k = tier - kits;
      if (this.el.fill) {
        setText(this.el.fill, tpl(s.str_fill_same, { k }));
        show(this.el.fill, !full && mainOk && k >= 2 && !!s.str_fill_same);
      }

      // v3.7: one hint line at the top. Incomplete → the gap text; complete → "add one more…" or done.
      if (this.el.full && !full) {
        setText(this.el.fullText, gapText(store));
        show(this.el.full, true);
        if (this.el.upgrade) show(this.el.upgrade, false);
      }
      if (this.el.full) this.el.full.classList.toggle('is-done', full);
      // all chosen
      if (this.el.full && full) {
        show(this.el.full, full);
        if (full) {
          const next = tier + 1;
          const canGrow = tier < cfg.maxTier;
          const dm = canGrow ? Engine.displayMode(cfg, next) : 'none';
          let text;
          if (canGrow && dm === 'cheapest_one' && !hide) {
            const fn = store.fill(next);   // 2026-10-22 sale prices: sale + bundle saving, as the tier card
            text = tpl(s.str_full_cheapest, { n: tier, m: next, amount: money(fn.sale + fn.saving) });
          } else if (canGrow && dm === 'all_kits') text = tpl(s.str_full, { n: tier, m: next, pct: Engine.pctFor(cfg, next) });
          else text = tpl(s.str_full_plain, { n: tier });
          setText(this.el.fullText, text);
          if (this.el.upgrade) {
            setText(this.el.upgrade, tpl(s.str_upgrade, { m: next }));
            show(this.el.upgrade, false); // v3.1: adding a kit grows the bundle by itself
          }
        }
      }

      // cards
      if (this.el.thisCard) this.syncCard(this.el.thisCard);
      if (this.el.list) this.el.list.querySelectorAll('[data-gbi-card]').forEach((li) => this.syncCard(li));

      // slots
      this.renderSlots(st, tier);

      // figures
      // 2026-10-22 sale prices: the strike is wasSum and "You save" = sale + bundle saving; the total
      // (and the add-to-cart label) stays what the customer pays.
      const bundleOk = Engine.displayMode(cfg, kits + store.inCart) !== 'none' && st.saving > 0 && !hide;
      const saleNow = st.sale > 0 && !hide ? st.sale : 0;
      const showSave = bundleOk || saleNow > 0;
      const before = (this.el.save ? this.el.save.textContent : '') + (this.el.total ? this.el.total.textContent : '');
      setText(this.el.save, tpl(s.picker_save_label || s.saving_label, { amount: money(saleNow + (bundleOk ? st.saving : 0)) }));
      show(this.el.save, showSave);
      setText(this.el.total, money(st.total));
      setText(this.el.was, money(st.wasSum));
      show(this.el.was, showSave);
      // 2026-10-22 sale prices: while the strike holds compare-at prices, screen readers skip it (as on the
      // tier cards; "You save" says the saving). Without a sale the markup stays exactly as before.
      if (this.el.was) {
        if (saleNow > 0) this.el.was.setAttribute('aria-hidden', 'true');
        else this.el.was.removeAttribute('aria-hidden');
      }
      const after = (this.el.save ? this.el.save.textContent : '') + (this.el.total ? this.el.total.textContent : '');
      if (before !== after && this.isOpen && this.el.figures && typeof this.el.figures.animate === 'function' && !reduced()) {
        this.el.figures.animate([{ opacity: 0.35 }, { opacity: 1 }], { duration: 160, easing: 'ease-out' });
      }

      // rule (≥750 only, CSS), gap, in-cart
      // v3.7: short rule under the "You save" pill ("15% off 1 kit"), only with a real saving
      const dmNow = Engine.displayMode(cfg, kits + store.inCart);
      const pctNow = Engine.pctFor(cfg, kits + store.inCart);
      let rule = '';
      if (dmNow === 'cheapest_one') rule = tpl(s.card_pct_cheapest || s.sub_cheapest, { pct: pctNow });
      else if (dmNow === 'all_kits') rule = tpl(s.card_pct_all || s.sub_all, { pct: pctNow });
      // 2026-10-22 sale prices: when the figure includes the sale, say so ("Sale price + 15% off 1 kit",
      // sale_rule; empty = hidden). No bundle rule (or no bundle saving) = no line, as before.
      if (rule && saleNow > 0) rule = s.sale_rule ? tpl(s.sale_rule, { rule }) : '';
      setText(this.el.rule, rule);
      show(this.el.rule, !!rule && bundleOk);
      show(this.el.gap, false);  // v3.7: the gap text is the hint line at the top
      const note = store.cartNote();
      if (note) setText(this.el.incart, inCartText(store));
      show(this.el.incart, note);

      // add to cart
      if (this.el.atc && !this.busy) {
        setText(this.el.atcLabel, kits > 1 ? tpl(s.str_atc_many, { k: kits, price: money(st.total) }) : tpl(s.str_atc_one, { price: money(st.total) }));
        this.el.atc.disabled = !mainOk;
      }
      this.prevKits = kits;
    }

    renderSlots(st, tier) {
      const ol = this.el.slots;
      if (!ol) return;
      const s = this.s;
      const sig = tier + '|' + st.units.map((u) => u.variantId + ':' + (u.image || '')).join(',');
      if (ol.dataset.sig === sig) return;
      const prevKits = Number(ol.dataset.kits || 0);
      ol.dataset.sig = sig;
      ol.dataset.kits = String(st.kits);
      const frag = document.createDocumentFragment();
      const thumb = (u) => {
        if (!u.image) return null;
        const img = document.createElement('img');
        img.src = u.image;
        img.alt = u.variantTitle ? u.title + ' – ' + u.variantTitle : u.title;
        img.width = 36; img.height = 36; img.decoding = 'async';
        return img;
      };
      for (let i = 0; i < tier; i += 1) {
        const li = document.createElement('li');
        li.className = 'gbi-picker__slot';
        const u = st.units[i];
        if (i === 0) {
          li.classList.add('is-main', 'is-filled');
          const img = thumb(u);
          if (img) li.appendChild(img);
          const num = document.createElement('span');
          num.className = 'gbi-picker__slot-num gbi-mono';
          num.setAttribute('aria-hidden', 'true');
          num.textContent = '1';
          li.appendChild(num);
        } else if (u) {
          li.classList.add('is-filled');
          if (prevKits && i >= prevKits) li.classList.add('is-new');
          const img = thumb(u);
          if (img) li.appendChild(img);
          const x = document.createElement('button');
          x.type = 'button';
          x.className = 'gbi-picker__slot-x';
          x.dataset.gbiSlotRemove = String(i - 1);
          x.setAttribute('aria-label', tpl(s.str_remove, { title: u.variantTitle ? u.title + ' – ' + u.variantTitle : u.title }));
          x.innerHTML = this.icons.close || '';
          li.appendChild(x);
        } else {
          li.classList.add('is-empty');
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'gbi-picker__slot-empty gbi-mono';
          b.setAttribute('data-gbi-slot-empty', '');
          b.setAttribute('aria-label', tpl(s.str_slot_empty, { x: i + 1 }));
          b.textContent = String(i + 1);
          li.appendChild(b);
        }
        frag.appendChild(li);
      }
      ol.replaceChildren(frag);
    }

    /* --- add to cart (§B4.3 Add) */
    hideError() {
      if (this.el.error) { this.el.error.hidden = true; this.el.error.textContent = ''; }
      this.querySelectorAll('.gbi-card.is-error').forEach((c) => c.classList.remove('is-error'));
    }

    addToCart() {
      const g = G();
      const store = this.store;
      if (this.busy || !store.mainAvailable()) return;
      store.remap();
      const st = store.state();
      const items = st.cartItems;
      const btn = this.el.atc;
      this.hideError();
      this.busy = true;
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
      setText(this.el.atcLabel, (g.strings && g.strings.adding) || this.el.atcLabel.textContent);
      const opener = (this.opener && this.opener.isConnected ? this.opener : null) || document.getElementById('GbiAtc-' + this.sid);
      const done = () => {
        this.busy = false;
        btn.removeAttribute('aria-busy');
        this.render();
      };
      if (!g.cart || typeof g.cart.add !== 'function') { done(); return; }
      g.cart.add(items, {
        opener,
        source: 'picker',
        sectionId: this.sid,
        beforeOpen: () => this.close('added')
      }).then(() => {
        track('bundle_add_to_cart', {
          kits: st.kits, tier: st.tier, value: st.total, currency: store.currency(), est_saving: st.saving,
          savings_mode: this.cfg.mode, items: items.map((x) => ({ id: x.id, qty: x.quantity })), source: 'picker'
        });
        if (this.dlg.open) this.close('added');
        store.reset();
        done();
      }).catch((err) => {
        const msg = (err && err.message) || (g.strings && g.strings.cartError) || '';
        done();
        if (this.el.error) { this.el.error.textContent = msg; this.el.error.hidden = !msg; }
        const low = msg.toLowerCase();
        this.querySelectorAll('[data-gbi-card]').forEach((c) => {
          const full = (c.dataset.fullTitle || '').toLowerCase();
          const h3 = c.querySelector('.gbi-card__title');
          const short = h3 ? h3.textContent.trim().toLowerCase() : '';
          if ((full && low.indexOf(full) !== -1) || (short && short.length > 3 && low.indexOf(short) !== -1)) c.classList.add('is-error');
        });
        track('bundle_add_error', { message: msg });
        this.refreshAvailability(st);
      });
    }

    /* After a rejected add: availability only, prices stay from Liquid */
    refreshAvailability(st) {
      const rootUrl = String(((G().config || {}).routes || {}).root || '/').replace(/\/$/, '');
      const handles = [];
      st.units.forEach((u) => {
        const json = this.store.cards.get(u.productId);
        const h = json && json.handle;
        if (h && handles.indexOf(h) === -1) handles.push(h);
      });
      handles.forEach((h) => {
        fetch(rootUrl + '/products/' + encodeURIComponent(h) + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => {
            if (!data || !Array.isArray(data.variants)) return;
            const json = this.store.cards.get(data.id);
            const main = this.store.product && Number(this.store.product.id) === Number(data.id) ? this.store.product : null;
            // The This-kit card registers its own JSON copy, so update the store's
            // main product too: mainAvailable()/mainVariant() read store.product.
            const targets = [json, main].filter((t, i, a) => t && Array.isArray(t.variants) && a.indexOf(t) === i);
            if (!targets.length) return;
            targets.forEach((t) => {
              data.variants.forEach((dv) => {
                const v = t.variants.find((x) => x.id === dv.id);
                if (v) v.available = !!dv.available;
              });
              t.available = t.variants.some((v) => v.available);
            });
            if (json) this.querySelectorAll('[data-gbi-card][data-product-id="' + data.id + '"]').forEach((c) => { c._gbiJSON = json; });
            if (main) this.store.emit(true);
            this.render();
          })
          .catch(() => {});
      });
    }
  }

  GbiBundleTiers.engine = Engine;
  GbiBundleTiers.Store = GbiBundleStore;
  if (!customElements.get('gbi-bundle-tiers')) customElements.define('gbi-bundle-tiers', GbiBundleTiers);
  if (!customElements.get('gbi-bundle-picker')) customElements.define('gbi-bundle-picker', GbiBundlePicker);
})();
